import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// ============================================================================
// NZ DATA
// ============================================================================
const NZ_CLIMATE_ZONES = {
  Auckland: {
    zone: "Subtropical Oceanic",
    meanAnnualTemp: 15.2,
    annualRainfall: 1213,
    freezeThawCycles: 0,
    uvIndex: "High",
    region: "Auckland",
    humidityAvg: 73,
    seismicZone: "High",
  },
  Wellington: {
    zone: "Temperate Maritime",
    meanAnnualTemp: 12.9,
    annualRainfall: 1249,
    freezeThawCycles: 5,
    uvIndex: "High",
    region: "Wellington",
    humidityAvg: 78,
    seismicZone: "Very High",
  },
  Christchurch: {
    zone: "Temperate Continental",
    meanAnnualTemp: 11.2,
    annualRainfall: 618,
    freezeThawCycles: 45,
    uvIndex: "Very High",
    region: "Canterbury",
    humidityAvg: 71,
    seismicZone: "Very High",
  },
  Queenstown: {
    zone: "Alpine Continental",
    meanAnnualTemp: 8.8,
    annualRainfall: 615,
    freezeThawCycles: 120,
    uvIndex: "Extreme",
    region: "Otago",
    humidityAvg: 65,
    seismicZone: "High",
  },
};

const NZ_ROAD_CLASSIFICATIONS = {
  "State Highway - Motorway": {
    designLife: 40,
    reliabilityLevel: 95,
    terminalServiceability: 2.0,
    ruttingLimit: 10,
    crackingLimit: 15,
    nztaCode: "SH-M",
    designSpeed: 100,
  },
  "State Highway - Primary": {
    designLife: 25,
    reliabilityLevel: 85,
    terminalServiceability: 2.5,
    ruttingLimit: 15,
    crackingLimit: 25,
    nztaCode: "SH-P",
    designSpeed: 80,
  },
  "Arterial Road": {
    designLife: 20,
    reliabilityLevel: 80,
    terminalServiceability: 2.5,
    ruttingLimit: 20,
    crackingLimit: 30,
    nztaCode: "AR",
    designSpeed: 60,
  },
  "Local Road": {
    designLife: 15,
    reliabilityLevel: 70,
    terminalServiceability: 2.0,
    ruttingLimit: 25,
    crackingLimit: 40,
    nztaCode: "LR",
    designSpeed: 50,
  },
};

const NZ_MATERIALS = {
  asphalt: {
    "AC14 Dense Grade": {
      modulus: 3200,
      cost: 145,
      carbonIntensity: 82,
      temperatureSusceptibility: 1.0,
    },
    "AC10 SMA (Premium)": {
      modulus: 4200,
      cost: 165,
      carbonIntensity: 95,
      temperatureSusceptibility: 0.8,
    },
    "Warm Mix AC14": {
      modulus: 3000,
      cost: 155,
      carbonIntensity: 68,
      temperatureSusceptibility: 0.9,
    },
  },
  basecourse: {
    "Greywacke AP40": { cbr: 150, cost: 35, carbonIntensity: 12 },
    "Basalt AP40": { cbr: 180, cost: 42, carbonIntensity: 15 },
    "Recycled GAP40": { cbr: 110, cost: 25, carbonIntensity: 6 },
  },
};

const NZ_TRAFFIC_GROWTH = {
  Auckland: 2.8,
  Wellington: 1.9,
  Canterbury: 2.1,
  Otago: 1.8,
};
const NZ_CARBON_PRICING = {
  current: 65,
  forecast: { 2025: 65, 2030: 95, 2035: 120, 2040: 150, 2050: 250 },
};

// CARBON COST MECHANISMS - Now editable
const DEFAULT_CARBON_MECHANISMS = {
  ets: {
    name: "NZ Emissions Trading Scheme (ETS)",
    description: "Market-based cap-and-trade system",
    currentPrice: 65,
    priceVolatility: "High (±30%)",
    forecast: { 2025: 65, 2030: 95, 2035: 120, 2040: 150, 2050: 250 },
    advantages: [
      "Market efficiency",
      "Price discovery",
      "Flexibility for emitters",
      "Revenue recycling potential",
    ],
    disadvantages: [
      "Price volatility",
      "Market manipulation risk",
      "Complex administration",
      "Uncertain long-term price",
    ],
    applicability: "All sectors covered under NZ ETS including transport fuels",
    complianceCost: 2.5,
  },
  carbonTax: {
    name: "Carbon Tax",
    description: "Fixed price per tonne CO₂e set by government",
    currentPrice: 75,
    priceVolatility: "Low (predictable)",
    forecast: { 2025: 75, 2030: 100, 2035: 130, 2040: 165, 2050: 280 },
    advantages: [
      "Price certainty",
      "Simple administration",
      "Predictable revenue",
      "Easy to understand",
    ],
    disadvantages: [
      "No emissions cap guarantee",
      "Political risk of rate changes",
      "Less flexibility",
      "Potential competitiveness concerns",
    ],
    applicability: "Applied at point of emission or fuel purchase",
    complianceCost: 1.0,
  },
  hybrid: {
    name: "Hybrid Approach (ETS + Price Floor/Ceiling)",
    description: "ETS with price stability mechanisms",
    currentPrice: 70,
    priceVolatility: "Medium (bounded)",
    forecast: { 2025: 70, 2030: 98, 2035: 125, 2040: 158, 2050: 265 },
    priceFloor: { 2025: 50, 2030: 70, 2035: 90, 2040: 110, 2050: 180 },
    priceCeiling: { 2025: 90, 2030: 130, 2035: 160, 2040: 200, 2050: 350 },
    advantages: [
      "Price stability",
      "Market efficiency",
      "Investment certainty",
      "Environmental outcome assurance",
    ],
    disadvantages: [
      "Complex design",
      "Potential market distortion",
      "Administrative burden",
      "Political negotiation required",
    ],
    applicability:
      "Recommended for infrastructure with long investment horizons",
    complianceCost: 2.0,
  },
};
const EN15804 = {
  A1: "Raw Material",
  A2: "Transport",
  A3: "Manufacturing",
  A4: "To Site",
  A5: "Construction",
  B1: "Use",
  B2: "Maintenance",
  C4: "Disposal",
  D: "Beyond",
};

// DEFAULT BITUMEN RHEOLOGY (REFERENCE)
const DEFAULT_BITUMEN = {
  name: "Standard Bitumen C320",
  penetration25C: 50,
  softeningPoint: 52,
  viscosity135C: 450,
  gStar25C: 15000,
  phaseAngle25C: 75,
  gStar_sinDelta: 1.5,
  mValue: 0.32,
  stiffness: 280,
  flashPoint: 250,
  pgGradeHigh: 64,
  pgGradeLow: -22,
  carbonIntensity: 450,
};

// BIOBINDER CANDIDATES
const BIOBINDER_CANDIDATES = [
  {
    id: "pine-resin",
    name: "Pine Resin Bio-binder",
    source: "NZ Forestry",
    penetration25C: 65,
    softeningPoint: 48,
    viscosity135C: 380,
    gStar25C: 12000,
    phaseAngle25C: 78,
    gStar_sinDelta: 1.1,
    mValue: 0.35,
    stiffness: 240,
    flashPoint: 220,
    pgGradeHigh: 58,
    pgGradeLow: -25,
    carbonIntensity: 180,
    bioContent: 30,
  },
  {
    id: "lignin-based",
    name: "Lignin-Modified Binder",
    source: "Pulp & Paper",
    penetration25C: 55,
    softeningPoint: 54,
    viscosity135C: 480,
    gStar25C: 16000,
    phaseAngle25C: 73,
    gStar_sinDelta: 1.6,
    mValue: 0.3,
    stiffness: 300,
    flashPoint: 245,
    pgGradeHigh: 64,
    pgGradeLow: -19,
    carbonIntensity: 220,
    bioContent: 25,
  },
  {
    id: "waste-cooking-oil",
    name: "Waste Cooking Oil",
    source: "Food Waste",
    penetration25C: 70,
    softeningPoint: 46,
    viscosity135C: 320,
    gStar25C: 10000,
    phaseAngle25C: 80,
    gStar_sinDelta: 0.9,
    mValue: 0.38,
    stiffness: 200,
    flashPoint: 210,
    pgGradeHigh: 52,
    pgGradeLow: -28,
    carbonIntensity: 120,
    bioContent: 40,
  },
  {
    id: "tallow-based",
    name: "NZ Tallow Bio-binder",
    source: "Meat Processing",
    penetration25C: 60,
    softeningPoint: 50,
    viscosity135C: 420,
    gStar25C: 13500,
    phaseAngle25C: 76,
    gStar_sinDelta: 1.3,
    mValue: 0.33,
    stiffness: 260,
    flashPoint: 235,
    pgGradeHigh: 58,
    pgGradeLow: -22,
    carbonIntensity: 150,
    bioContent: 35,
  },
];

// PERFORMANCE MODELS - Now includes biobinder rheology effects
const calculateRutting = (traffic, climate, materials, binderProps, years) =>
  years.map((year) => {
    const trafficFactor = Math.pow(
      (traffic.designESA * year) / traffic.designLife / 1000000,
      0.3
    );
    const tempFactor = 1 + (climate.meanAnnualTemp - 12) * 0.025;
    const uvFactor =
      climate.uvIndex === "Extreme"
        ? 1.25
        : climate.uvIndex === "Very High"
        ? 1.15
        : 1.05;

    // Biobinder rheology effect on rutting
    // G*/sin(δ) is the rutting parameter - higher is better (more rut resistant)
    // Standard bitumen has 1.5 kPa, if biobinder has lower, rutting increases
    const ruttingParameterRatio =
      DEFAULT_BITUMEN.gStar_sinDelta /
      (binderProps.gStar_sinDelta || DEFAULT_BITUMEN.gStar_sinDelta);
    const binderRuttingFactor = binderProps.isBiobinder
      ? Math.pow(ruttingParameterRatio, 0.5)
      : 1.0;

    // Softening point effect - lower softening point = more susceptible to high temps
    const softeningDiff =
      DEFAULT_BITUMEN.softeningPoint -
      (binderProps.softeningPoint || DEFAULT_BITUMEN.softeningPoint);
    const softeningFactor = 1 + softeningDiff * 0.02; // 2% increase per degree lower

    const rutting =
      trafficFactor *
      tempFactor *
      uvFactor *
      (materials.temperatureSusceptibility || 1) *
      binderRuttingFactor *
      softeningFactor *
      3.5;

    return {
      year,
      rutting: Math.min(Math.max(rutting, 0), 30),
      limit: traffic.ruttingLimit || 15,
      binderEffect: binderProps.isBiobinder
        ? binderRuttingFactor * softeningFactor
        : 1.0,
    };
  });

const calculateCracking = (traffic, climate, binderProps, years) =>
  years.map((year) => {
    const loadingCycles = year * traffic.dailyTrucks * 365;
    let crackingPercent = Math.min(
      100 * (1 - Math.pow(10, 15.676 - 2.5 * Math.log10(loadingCycles || 1))),
      50
    );
    crackingPercent *= climate.freezeThawCycles > 20 ? 1.3 : 1.0;

    // Biobinder rheology effect on cracking
    // m-value (BBR creep rate) - higher is better for low temp cracking resistance
    // Stiffness (BBR) - lower is better for low temp cracking
    const mValueRatio =
      (binderProps.mValue || DEFAULT_BITUMEN.mValue) / DEFAULT_BITUMEN.mValue;
    const stiffnessRatio =
      DEFAULT_BITUMEN.stiffness /
      (binderProps.stiffness || DEFAULT_BITUMEN.stiffness);
    const binderCrackingFactor = binderProps.isBiobinder
      ? 1 / (mValueRatio * stiffnessRatio)
      : 1.0;

    // PG Low grade effect - lower (more negative) is better for cold climates
    const pgLowDiff =
      (binderProps.pgGradeLow || DEFAULT_BITUMEN.pgGradeLow) -
      DEFAULT_BITUMEN.pgGradeLow;
    const coldClimateFactor =
      climate.freezeThawCycles > 20 ? 1 + pgLowDiff * 0.02 : 1.0;

    return {
      year,
      cracking: Math.max(
        crackingPercent * binderCrackingFactor * coldClimateFactor,
        0
      ),
      limit: traffic.crackingLimit || 25,
      binderEffect: binderProps.isBiobinder
        ? binderCrackingFactor * coldClimateFactor
        : 1.0,
    };
  });

const calculateRoughness = (
  traffic,
  climate,
  binderProps,
  years,
  initialIRI = 1.5
) =>
  years.map((year) => {
    const factors =
      (climate.annualRainfall > 1000 ? 1.2 : 1.0) *
      (climate.freezeThawCycles > 20 ? 1.25 : 1.0);

    // Biobinder effect on roughness progression
    // Penetration affects flexibility - higher penetration = more flexible = potentially faster roughness in heavy traffic
    const penetrationRatio =
      (binderProps.penetration25C || DEFAULT_BITUMEN.penetration25C) /
      DEFAULT_BITUMEN.penetration25C;
    const binderRoughnessFactor = binderProps.isBiobinder
      ? Math.pow(penetrationRatio, 0.15)
      : 1.0;

    const naasra =
      15 *
      Math.log(year + 1) *
      factors *
      Math.pow(traffic.designESA / 1000000, 0.12) *
      binderRoughnessFactor;

    return {
      year,
      iri: initialIRI + naasra / 30,
      naasra,
      limit: traffic.terminalServiceability || 2.0,
      binderEffect: binderRoughnessFactor,
    };
  });

// STYLES
const S = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #1e40af, #3b82f6, #6366f1)",
    padding: "1rem",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  mainCard: {
    maxWidth: "1600px",
    margin: "0 auto",
    background: "rgba(255,255,255,0.98)",
    borderRadius: "1.5rem",
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(135deg, #1e40af, #7c3aed)",
    color: "white",
    padding: "2rem",
  },
  headerTitle: {
    fontSize: "2.25rem",
    fontWeight: "bold",
    marginBottom: "0.5rem",
  },
  nav: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
    padding: "1rem",
    background: "#f3f4f6",
    justifyContent: "center",
  },
  navBtn: (active) => ({
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.75rem 1.25rem",
    borderRadius: "0.75rem",
    border: "none",
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: "600",
    background: active
      ? "linear-gradient(135deg, #1e40af, #7c3aed)"
      : "#e5e7eb",
    color: active ? "white" : "#374151",
  }),
  content: { padding: "2rem" },
  sectionTitle: {
    fontSize: "1.75rem",
    fontWeight: "bold",
    marginBottom: "0.5rem",
    background: "linear-gradient(135deg, #1e40af, #7c3aed)",
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  sectionSub: { fontSize: "1rem", color: "#6b7280", marginBottom: "1.5rem" },
  card: {
    background: "white",
    borderRadius: "1rem",
    border: "1px solid #e5e7eb",
    padding: "1.5rem",
    marginBottom: "1.5rem",
  },
  cardTitle: {
    fontSize: "1.125rem",
    fontWeight: "bold",
    marginBottom: "1rem",
    color: "#111827",
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "1.5rem",
  },
  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "1rem",
  },
  grid4: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "1rem",
  },
  input: {
    width: "100%",
    padding: "0.75rem",
    border: "2px solid #d1d5db",
    borderRadius: "0.5rem",
    fontSize: "1rem",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "0.75rem",
    border: "2px solid #d1d5db",
    borderRadius: "0.5rem",
    fontSize: "1rem",
    background: "white",
    boxSizing: "border-box",
  },
  label: {
    display: "block",
    fontSize: "0.875rem",
    fontWeight: "600",
    marginBottom: "0.5rem",
    color: "#374151",
  },
  btnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.75rem 1.5rem",
    background: "linear-gradient(135deg, #1e40af, #7c3aed)",
    color: "white",
    border: "none",
    borderRadius: "0.75rem",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
  },
  btnSecondary: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.75rem 1.5rem",
    background: "white",
    color: "#374151",
    border: "2px solid #d1d5db",
    borderRadius: "0.75rem",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
  },
  metricCard: (c) => ({
    padding: "1.25rem",
    borderRadius: "1rem",
    borderLeft: `4px solid ${
      c === "red"
        ? "#dc2626"
        : c === "green"
        ? "#059669"
        : c === "orange"
        ? "#d97706"
        : c === "purple"
        ? "#7c3aed"
        : "#1e40af"
    }`,
    background:
      c === "red"
        ? "rgba(220,38,38,0.05)"
        : c === "green"
        ? "rgba(5,150,105,0.05)"
        : c === "orange"
        ? "rgba(217,119,6,0.05)"
        : c === "purple"
        ? "rgba(124,58,237,0.05)"
        : "rgba(30,64,175,0.05)",
  }),
  metricValue: { fontSize: "1.75rem", fontWeight: "bold", color: "#111827" },
  metricLabel: { fontSize: "0.875rem", fontWeight: "500", color: "#6b7280" },
  metricUnit: { fontSize: "0.75rem", color: "#9ca3af" },
  infoBox: (t) => ({
    padding: "1rem",
    borderRadius: "0.75rem",
    background:
      t === "info" ? "#eff6ff" : t === "success" ? "#ecfdf5" : "#fffbeb",
    border: `1px solid ${
      t === "info" ? "#93c5fd" : t === "success" ? "#6ee7b7" : "#fcd34d"
    }`,
    color: t === "info" ? "#1e40af" : t === "success" ? "#065f46" : "#92400e",
    fontSize: "0.875rem",
  }),
  badge: (c) => ({
    display: "inline-flex",
    padding: "0.25rem 0.75rem",
    borderRadius: "9999px",
    fontSize: "0.75rem",
    fontWeight: "600",
    background:
      c === "green"
        ? "#dcfce7"
        : c === "orange"
        ? "#fef3c7"
        : c === "red"
        ? "#fee2e2"
        : "#dbeafe",
    color:
      c === "green"
        ? "#166534"
        : c === "orange"
        ? "#92400e"
        : c === "red"
        ? "#991b1b"
        : "#1e40af",
  }),
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" },
  th: {
    textAlign: "left",
    padding: "0.75rem",
    fontWeight: "600",
    color: "#374151",
    background: "#f3f4f6",
    borderBottom: "1px solid #e5e7eb",
  },
  td: {
    padding: "0.75rem",
    borderBottom: "1px solid #f3f4f6",
    color: "#4b5563",
  },
  emptyState: {
    textAlign: "center",
    padding: "3rem",
    background: "#f9fafb",
    borderRadius: "1rem",
    border: "2px dashed #d1d5db",
  },
};

// COMPONENTS
const MetricCard = ({ title, value, unit, color = "blue", icon }) => (
  <div style={S.metricCard(color)}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
    >
      <div>
        <div style={S.metricLabel}>{title}</div>
        <div style={S.metricValue}>{value}</div>
        {unit && <div style={S.metricUnit}>{unit}</div>}
      </div>
      {icon && <span style={{ fontSize: "1.5rem", opacity: 0.7 }}>{icon}</span>}
    </div>
  </div>
);

const InputField = ({
  label,
  value,
  onChange,
  type = "number",
  unit,
  helpText,
  step,
}) => (
  <div style={{ marginBottom: "1rem" }}>
    <label style={S.label}>
      {label}
      {unit && (
        <span
          style={{
            fontWeight: "normal",
            color: "#9ca3af",
            marginLeft: "0.25rem",
          }}
        >
          ({unit})
        </span>
      )}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) =>
        onChange(type === "number" ? Number(e.target.value) : e.target.value)
      }
      step={step}
      style={S.input}
    />
    {helpText && (
      <div
        style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "0.25rem" }}
      >
        {helpText}
      </div>
    )}
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div style={{ marginBottom: "1rem" }}>
    <label style={S.label}>{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={S.select}
    >
      {options.map((opt) => (
        <option key={opt.value || opt} value={opt.value || opt}>
          {opt.label || opt}
        </option>
      ))}
    </select>
  </div>
);

// MAIN APP
export default function EcoRoadsNZ() {
  const [activeTab, setActiveTab] = useState("design");
  const [nzLocation, setNzLocation] = useState("Auckland");
  const [roadClassification, setRoadClassification] = useState(
    "State Highway - Primary"
  );
  const [asphaltType, setAsphaltType] = useState("AC14 Dense Grade");
  const [basecourseType, setBasecourseType] = useState("Greywacke AP40");

  const climateData = NZ_CLIMATE_ZONES[nzLocation];
  const roadStandards = NZ_ROAD_CLASSIFICATIONS[roadClassification];
  const selectedAsphalt = NZ_MATERIALS.asphalt[asphaltType];
  const selectedBasecourse = NZ_MATERIALS.basecourse[basecourseType];
  const regionalGrowthRate = NZ_TRAFFIC_GROWTH[climateData.region] || 2.0;

  const [designParams, setDesignParams] = useState({
    designLife: 25,
    reliabilityLevel: 85,
    initialServiceability: 4.0,
    terminalServiceability: 2.5,
  });
  const [materialProps, setMaterialProps] = useState({
    asphaltThickness: 100,
    baseThickness: 300,
  });
  const [trafficParams, setTrafficParams] = useState({
    initialADT: 5000,
    truckPercentage: 15,
    growthRate: 2.8,
    directionFactor: 0.5,
    laneFactor: 0.8,
  });
  const [performanceResults, setPerformanceResults] = useState({
    rutting: [],
    cracking: [],
    roughness: [],
  });

  // Biobinder State
  const [selectedBiobinder, setSelectedBiobinder] = useState(null);
  const [useBiobinderInAnalysis, setUseBiobinderInAnalysis] = useState(false);
  const [biobinderBlendRatio, setBiobinderBlendRatio] = useState(100); // % of binder replaced
  const [customBiobinder, setCustomBiobinder] = useState({
    name: "Custom Bio-binder",
    penetration25C: 60,
    softeningPoint: 50,
    viscosity135C: 400,
    gStar25C: 13000,
    phaseAngle25C: 76,
    gStar_sinDelta: 1.2,
    mValue: 0.33,
    stiffness: 260,
    flashPoint: 230,
    pgGradeHigh: 58,
    pgGradeLow: -22,
    carbonIntensity: 200,
    bioContent: 25,
  });

  // Get current biobinder properties
  const currentBiobinder = useMemo(() => {
    if (selectedBiobinder) {
      return (
        BIOBINDER_CANDIDATES.find((b) => b.id === selectedBiobinder) ||
        customBiobinder
      );
    }
    return customBiobinder;
  }, [selectedBiobinder, customBiobinder]);

  // Calculate blended binder properties (for performance modeling)
  const effectiveBinderProps = useMemo(() => {
    if (!useBiobinderInAnalysis) {
      return {
        name: "Standard Bitumen C320",
        carbonIntensity: DEFAULT_BITUMEN.carbonIntensity,
        gStar_sinDelta: DEFAULT_BITUMEN.gStar_sinDelta,
        mValue: DEFAULT_BITUMEN.mValue,
        stiffness: DEFAULT_BITUMEN.stiffness,
        softeningPoint: DEFAULT_BITUMEN.softeningPoint,
        penetration25C: DEFAULT_BITUMEN.penetration25C,
        pgGradeHigh: DEFAULT_BITUMEN.pgGradeHigh,
        pgGradeLow: DEFAULT_BITUMEN.pgGradeLow,
        isBiobinder: false,
        blendRatio: 0,
      };
    }

    // Blend properties based on ratio
    const ratio = biobinderBlendRatio / 100;
    const blend = (bitumenVal, bioVal) =>
      bitumenVal * (1 - ratio) + bioVal * ratio;

    return {
      name:
        ratio === 1
          ? currentBiobinder.name
          : `Blended (${biobinderBlendRatio}% ${currentBiobinder.name})`,
      carbonIntensity: blend(
        DEFAULT_BITUMEN.carbonIntensity,
        currentBiobinder.carbonIntensity
      ),
      gStar_sinDelta: blend(
        DEFAULT_BITUMEN.gStar_sinDelta,
        currentBiobinder.gStar_sinDelta
      ),
      mValue: blend(DEFAULT_BITUMEN.mValue, currentBiobinder.mValue),
      stiffness: blend(DEFAULT_BITUMEN.stiffness, currentBiobinder.stiffness),
      softeningPoint: blend(
        DEFAULT_BITUMEN.softeningPoint,
        currentBiobinder.softeningPoint
      ),
      penetration25C: blend(
        DEFAULT_BITUMEN.penetration25C,
        currentBiobinder.penetration25C
      ),
      pgGradeHigh: Math.min(
        DEFAULT_BITUMEN.pgGradeHigh,
        currentBiobinder.pgGradeHigh
      ),
      pgGradeLow: Math.max(
        DEFAULT_BITUMEN.pgGradeLow,
        currentBiobinder.pgGradeLow
      ),
      isBiobinder: true,
      blendRatio: biobinderBlendRatio,
      bioContent: currentBiobinder.bioContent * ratio,
    };
  }, [useBiobinderInAnalysis, biobinderBlendRatio, currentBiobinder]);

  // LCA State
  const [lcaEntries, setLcaEntries] = useState([]);
  const [lcaFactors, setLcaFactors] = useState({});
  const [showLcaDialog, setShowLcaDialog] = useState(false);
  const [lcaForm, setLcaForm] = useState({
    title: "",
    mass: "",
    label: "",
    customEpd: "",
    scope: "",
  });
  const [activeLcaModule, setActiveLcaModule] = useState(null);
  const fileInputRef = useRef(null);

  // Auto-generated LCA entries from Materials/Carbon tab
  const autoLcaEntries = useMemo(() => {
    const entries = [];

    // Calculate material masses (assuming 1km road length, 3.5m lane width, typical densities)
    const roadLength = 1000; // 1km default
    const laneWidth = 3.5; // meters
    const asphaltDensity = 2.4; // tonnes/m³
    const basecourseDensity = 2.2; // tonnes/m³

    // Asphalt layer
    const asphaltVolume =
      (materialProps.asphaltThickness / 1000) * laneWidth * roadLength;
    const asphaltMass = asphaltVolume * asphaltDensity * 1000; // kg

    // Basecourse layer
    const basecourseVolume =
      (materialProps.baseThickness / 1000) * laneWidth * roadLength;
    const basecourseMass = basecourseVolume * basecourseDensity * 1000; // kg
    const basecourseCarbon =
      (basecourseMass / 1000) * selectedBasecourse.carbonIntensity;

    // Binder carbon intensity (uses biobinder if enabled)
    const binderCarbonIntensity = effectiveBinderProps.carbonIntensity;
    const binderMass = asphaltMass * 0.05; // ~5% binder content
    const binderCarbon = (binderMass / 1000) * binderCarbonIntensity;

    // A1: Raw Material Supply - Bitumen/Binder
    entries.push({
      id: "auto-a1-bitumen",
      module: "A1",
      title: useBiobinderInAnalysis
        ? `${effectiveBinderProps.name} (${biobinderBlendRatio}% bio-replacement)`
        : `Bitumen/Binder (Standard C320)`,
      mass: Math.round(binderMass),
      ef: binderCarbonIntensity,
      total: Math.round(binderCarbon),
      scope: "3",
      isAuto: true,
      source: useBiobinderInAnalysis ? "Biobinder Tab" : "Carbon Tab",
    });

    // A1: Raw Material Supply - Aggregates for Asphalt
    const aggregateMass = asphaltMass * 0.95;
    const aggregateCarbon = (aggregateMass / 1000) * 5; // ~5 kgCO2e/t for aggregates
    entries.push({
      id: "auto-a1-agg-asphalt",
      module: "A1",
      title: `Aggregates for Asphalt Mix`,
      mass: Math.round(aggregateMass),
      ef: 5,
      total: Math.round(aggregateCarbon),
      scope: "3",
      isAuto: true,
      source: "Carbon Tab",
    });

    // A1: Raw Material Supply - Basecourse Aggregates
    entries.push({
      id: "auto-a1-basecourse",
      module: "A1",
      title: `Basecourse Material (${basecourseType})`,
      mass: Math.round(basecourseMass),
      ef: selectedBasecourse.carbonIntensity,
      total: Math.round(basecourseCarbon),
      scope: "3",
      isAuto: true,
      source: "Carbon Tab",
    });

    // A3: Manufacturing - Asphalt Plant (mixing energy)
    const mixingCarbon = (asphaltMass / 1000) * 35; // ~35 kgCO2e/t for plant mixing
    entries.push({
      id: "auto-a3-plant",
      module: "A3",
      title: `Asphalt Plant Production (${asphaltType})`,
      mass: Math.round(asphaltMass),
      ef: 35,
      total: Math.round(mixingCarbon),
      scope: "1",
      isAuto: true,
      source: "Carbon Tab",
    });

    return entries;
  }, [
    materialProps,
    selectedAsphalt,
    selectedBasecourse,
    asphaltType,
    basecourseType,
    effectiveBinderProps,
    useBiobinderInAnalysis,
    biobinderBlendRatio,
  ]);

  // Combined LCA entries (auto + manual)
  const allLcaEntries = useMemo(() => {
    return [...autoLcaEntries, ...lcaEntries];
  }, [autoLcaEntries, lcaEntries]);

  // Economic State
  const [economicInputs, setEconomicInputs] = useState({
    initialInvestment: 500000,
    projectLifespan: 20,
    discountRate: 6,
    annualSavings: 50000,
    annualOperatingCost: 10000,
  });
  const [carbonMechanism, setCarbonMechanism] = useState("ets");
  const [showCarbonDetails, setShowCarbonDetails] = useState(false);

  // Editable Carbon Mechanisms
  const [carbonMechanisms, setCarbonMechanisms] = useState(
    JSON.parse(JSON.stringify(DEFAULT_CARBON_MECHANISMS))
  );
  const [showMechanismEditor, setShowMechanismEditor] = useState(false);
  const [editingMechanism, setEditingMechanism] = useState(null);

  // Export State
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState("html");
  const [exportSections, setExportSections] = useState({
    design: true,
    traffic: true,
    materials: true,
    biobinder: true,
    performance: true,
    carbon: true,
    lca: true,
    economic: true,
  });

  useEffect(() => {
    const standards = NZ_ROAD_CLASSIFICATIONS[roadClassification];
    setDesignParams((p) => ({
      ...p,
      designLife: standards.designLife,
      reliabilityLevel: standards.reliabilityLevel,
    }));
  }, [roadClassification]);

  useEffect(() => {
    setTrafficParams((p) => ({
      ...p,
      growthRate: NZ_TRAFFIC_GROWTH[NZ_CLIMATE_ZONES[nzLocation].region] || 2.0,
    }));
  }, [nzLocation]);

  const trafficAnalysis = useMemo(() => {
    const designADT =
      trafficParams.initialADT *
      Math.pow(1 + trafficParams.growthRate / 100, designParams.designLife / 2);
    const dailyTrucks =
      designADT *
      (trafficParams.truckPercentage / 100) *
      trafficParams.directionFactor *
      trafficParams.laneFactor;
    return {
      designADT: Math.round(designADT),
      dailyTrucks: Math.round(dailyTrucks),
      designESA: Math.round(dailyTrucks * 365 * designParams.designLife * 2.5),
      designLife: designParams.designLife,
      ruttingLimit: roadStandards.ruttingLimit,
      crackingLimit: roadStandards.crackingLimit,
      terminalServiceability: roadStandards.terminalServiceability,
    };
  }, [trafficParams, designParams, roadStandards]);

  const carbonAnalysis = useMemo(() => {
    // Calculate binder carbon based on whether biobinder is used
    const binderContent = 0.05; // 5% binder in asphalt mix
    const asphaltVolume = (materialProps.asphaltThickness / 1000) * 3.5 * 1000; // m³ per km
    const asphaltMass = asphaltVolume * 2.4; // tonnes
    const binderMass = asphaltMass * binderContent;
    const aggregateMass = asphaltMass * (1 - binderContent);

    // Binder carbon (uses biobinder if enabled)
    const binderCarbonIntensity = effectiveBinderProps.carbonIntensity;
    const binderCarbon = binderMass * binderCarbonIntensity;

    // Standard bitumen carbon for comparison
    const standardBinderCarbon = binderMass * DEFAULT_BITUMEN.carbonIntensity;
    const binderCarbonSavings = standardBinderCarbon - binderCarbon;

    // Aggregate carbon (from selected asphalt type - represents aggregate + mixing)
    const aggregateAndMixingCarbon =
      (asphaltMass / 1000) * selectedAsphalt.carbonIntensity;

    // Total asphalt carbon
    const asphaltCarbon = binderCarbon + aggregateAndMixingCarbon;

    // Basecourse carbon
    const basecourseVolume = (materialProps.baseThickness / 1000) * 3.5 * 1000;
    const basecourseMass = basecourseVolume * 2.2;
    const basecourseCarbon =
      (basecourseMass / 1000) * selectedBasecourse.carbonIntensity;

    const total = asphaltCarbon + basecourseCarbon;
    const totalPerMeter = total / 1000; // per meter of road

    return {
      totalCarbon: totalPerMeter.toFixed(1),
      asphaltCarbon: (asphaltCarbon / 1000).toFixed(1),
      basecourseCarbon: (basecourseCarbon / 1000).toFixed(1),
      binderCarbon: (binderCarbon / 1000).toFixed(2),
      binderCarbonSavings: (binderCarbonSavings / 1000).toFixed(2),
      binderCarbonSavingsPercent:
        standardBinderCarbon > 0
          ? ((binderCarbonSavings / standardBinderCarbon) * 100).toFixed(1)
          : 0,
      carbonCostCurrent: (totalPerMeter * NZ_CARBON_PRICING.current).toFixed(0),
      carbonCost2030: (
        totalPerMeter * NZ_CARBON_PRICING.forecast[2030]
      ).toFixed(0),
      useBiobinder: useBiobinderInAnalysis,
      binderName: effectiveBinderProps.name,
      totalProjectCarbon: total, // Full project carbon in kg
    };
  }, [
    materialProps,
    selectedAsphalt,
    selectedBasecourse,
    effectiveBinderProps,
    useBiobinderInAnalysis,
  ]);

  const lcaTotalEmissions = allLcaEntries.reduce((sum, e) => sum + e.total, 0);
  const lcaBreakdown = {
    scope1: allLcaEntries
      .filter((e) => e.scope === "1")
      .reduce((s, e) => s + e.total, 0),
    scope2: allLcaEntries
      .filter((e) => e.scope === "2")
      .reduce((s, e) => s + e.total, 0),
    scope3: allLcaEntries
      .filter((e) => e.scope === "3")
      .reduce((s, e) => s + e.total, 0),
  };
  const lcaAutoTotal = autoLcaEntries.reduce((sum, e) => sum + e.total, 0);
  const lcaManualTotal = lcaEntries.reduce((sum, e) => sum + e.total, 0);

  const economicAnalysis = useMemo(() => {
    const mechanism = carbonMechanisms[carbonMechanism];
    const baseCarbon =
      lcaTotalEmissions > 0
        ? lcaTotalEmissions
        : parseFloat(carbonAnalysis.totalCarbon) * 1000; // Convert to project scale

    // Calculate year-by-year carbon costs based on mechanism forecasts
    const yearlyData = [];
    let totalCarbonCost = 0;
    let totalDiscountedCarbonCost = 0;

    for (let y = 0; y <= economicInputs.projectLifespan; y++) {
      const year = 2025 + y;
      const forecastYear =
        year <= 2025
          ? 2025
          : year <= 2030
          ? 2030
          : year <= 2035
          ? 2035
          : year <= 2040
          ? 2040
          : 2050;
      const carbonPrice =
        mechanism.forecast[forecastYear] || mechanism.currentPrice;
      const annualCarbon = baseCarbon / economicInputs.projectLifespan;
      const annualCarbonCost =
        (annualCarbon / 1000) *
        carbonPrice *
        (1 + mechanism.complianceCost / 100);
      const discountedCost =
        annualCarbonCost / Math.pow(1 + economicInputs.discountRate / 100, y);

      if (y > 0) {
        totalCarbonCost += annualCarbonCost;
        totalDiscountedCarbonCost += discountedCost;
      }

      yearlyData.push({
        year,
        carbonPrice,
        annualCarbonCost: y === 0 ? 0 : annualCarbonCost,
        cumulativeCost: totalCarbonCost,
        discountedCost: y === 0 ? 0 : discountedCost,
      });
    }

    const carbonCostAnnual = totalCarbonCost / economicInputs.projectLifespan;
    const cashFlows = Array.from(
      { length: economicInputs.projectLifespan + 1 },
      (_, y) =>
        y === 0
          ? -economicInputs.initialInvestment
          : economicInputs.annualSavings -
            economicInputs.annualOperatingCost -
            yearlyData[y].annualCarbonCost
    );
    const npv = cashFlows.reduce(
      (sum, f, y) =>
        sum + f / Math.pow(1 + economicInputs.discountRate / 100, y),
      0
    );
    const totalCash = cashFlows.slice(1).reduce((s, f) => s + f, 0);
    let payback = economicInputs.projectLifespan,
      cum = -economicInputs.initialInvestment;
    for (let y = 1; y <= economicInputs.projectLifespan; y++) {
      cum += cashFlows[y];
      if (cum >= 0) {
        payback = y;
        break;
      }
    }

    // Calculate comparison across all mechanisms
    const mechanismComparison = Object.entries(carbonMechanisms).map(
      ([key, mech]) => {
        let mechTotalCost = 0;
        for (let y = 1; y <= economicInputs.projectLifespan; y++) {
          const year = 2025 + y;
          const forecastYear =
            year <= 2025
              ? 2025
              : year <= 2030
              ? 2030
              : year <= 2035
              ? 2035
              : year <= 2040
              ? 2040
              : 2050;
          const price = mech.forecast[forecastYear] || mech.currentPrice;
          mechTotalCost +=
            (baseCarbon / economicInputs.projectLifespan / 1000) *
            price *
            (1 + mech.complianceCost / 100);
        }
        return {
          key,
          name: mech.name,
          totalCost: mechTotalCost,
          avgAnnual: mechTotalCost / economicInputs.projectLifespan,
        };
      }
    );

    return {
      npv,
      irr:
        (totalCash /
          economicInputs.projectLifespan /
          economicInputs.initialInvestment) *
        100,
      paybackPeriod: payback,
      bcr: totalCash / economicInputs.initialInvestment,
      totalCarbonCost,
      totalDiscountedCarbonCost,
      carbonCostAnnual,
      yearlyData,
      mechanismComparison,
      mechanism,
    };
  }, [
    economicInputs,
    lcaTotalEmissions,
    carbonAnalysis,
    carbonMechanism,
    carbonMechanisms,
  ]);

  const runNZAnalysis = () => {
    const years = Array.from(
      { length: designParams.designLife + 1 },
      (_, i) => i
    );
    setPerformanceResults({
      rutting: calculateRutting(
        trafficAnalysis,
        climateData,
        selectedAsphalt,
        effectiveBinderProps,
        years
      ),
      cracking: calculateCracking(
        trafficAnalysis,
        climateData,
        effectiveBinderProps,
        years
      ),
      roughness: calculateRoughness(
        trafficAnalysis,
        climateData,
        effectiveBinderProps,
        years,
        designParams.initialServiceability
      ),
      binderUsed: effectiveBinderProps,
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text(),
        lines = text.split("\n").filter((l) => l.trim());
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const labelIdx = headers.findIndex((h) =>
        ["label", "material", "name"].some((k) => h.includes(k))
      );
      const factorIdx = headers.findIndex((h) =>
        ["factor", "emission", "co2"].some((k) => h.includes(k))
      );
      if (labelIdx === -1 || factorIdx === -1)
        throw new Error("Missing columns");
      const factors = {};
      for (let i = 1; i < lines.length; i++) {
        const v = lines[i].split(",").map((x) => x.trim());
        if (v[labelIdx] && !isNaN(parseFloat(v[factorIdx])))
          factors[v[labelIdx]] = parseFloat(v[factorIdx]);
      }
      setLcaFactors(factors);
      alert(`Loaded ${Object.keys(factors).length} emission factors`);
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onModuleClick = (mod) => {
    setActiveLcaModule(mod);
    setLcaForm({ title: "", mass: "", label: "", customEpd: "", scope: "" });
    setShowLcaDialog(true);
  };
  const addLcaEntry = () => {
    const { title, mass, label, customEpd, scope } = lcaForm;
    if (!mass || (!label && !customEpd) || !scope) {
      alert("Fill all fields");
      return;
    }
    const m = parseFloat(mass),
      ef = customEpd ? parseFloat(customEpd) : lcaFactors[label] || 0;
    setLcaEntries((p) => [
      ...p,
      {
        module: activeLcaModule,
        title,
        mass: m,
        label: label || `Custom (${ef})`,
        ef,
        total: +(ef * m).toFixed(3),
        scope,
      },
    ]);
    setShowLcaDialog(false);
  };

  // Export Report Function
  const generateReport = () => {
    const currentDate = new Date().toLocaleDateString("en-NZ", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const projectName = `${nzLocation} - ${roadClassification}`;

    const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>ecoroadsNZ Report - ${projectName}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #1f2937; background: #f9fafb; }
    .container { max-width: 1000px; margin: 0 auto; padding: 2rem; }
    .header { background: linear-gradient(135deg, #1e40af, #7c3aed); color: white; padding: 2rem; border-radius: 1rem; margin-bottom: 2rem; }
    .header h1 { font-size: 2rem; margin-bottom: 0.5rem; }
    .header p { opacity: 0.9; }
    .section { background: white; border-radius: 1rem; padding: 1.5rem; margin-bottom: 1.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    .section h2 { font-size: 1.25rem; color: #1e40af; border-bottom: 2px solid #e5e7eb; padding-bottom: 0.5rem; margin-bottom: 1rem; }
    .section h3 { font-size: 1rem; color: #374151; margin: 1rem 0 0.5rem 0; }
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin: 1rem 0; }
    .metric { padding: 1rem; background: #f3f4f6; border-radius: 0.5rem; border-left: 4px solid #1e40af; }
    .metric.green { border-left-color: #059669; background: #ecfdf5; }
    .metric.red { border-left-color: #dc2626; background: #fef2f2; }
    .metric.orange { border-left-color: #d97706; background: #fffbeb; }
    .metric.purple { border-left-color: #7c3aed; background: #f5f3ff; }
    .metric-label { font-size: 0.75rem; color: #6b7280; text-transform: uppercase; }
    .metric-value { font-size: 1.5rem; font-weight: bold; color: #111827; }
    .metric-unit { font-size: 0.75rem; color: #9ca3af; }
    table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.875rem; }
    th, td { padding: 0.75rem; text-align: left; border-bottom: 1px solid #e5e7eb; }
    th { background: #f3f4f6; font-weight: 600; color: #374151; }
    .badge { display: inline-block; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 600; }
    .badge-green { background: #dcfce7; color: #166534; }
    .badge-orange { background: #fef3c7; color: #92400e; }
    .badge-blue { background: #dbeafe; color: #1e40af; }
    .badge-purple { background: #ede9fe; color: #6b21a8; }
    .badge-red { background: #fee2e2; color: #991b1b; }
    .info-box { padding: 1rem; border-radius: 0.5rem; margin: 1rem 0; }
    .info-box.success { background: #ecfdf5; border: 1px solid #6ee7b7; color: #065f46; }
    .info-box.warning { background: #fffbeb; border: 1px solid #fcd34d; color: #92400e; }
    .info-box.info { background: #eff6ff; border: 1px solid #93c5fd; color: #1e40af; }
    .footer { text-align: center; padding: 2rem; color: #6b7280; font-size: 0.875rem; }
    .toc { background: #f3f4f6; padding: 1rem; border-radius: 0.5rem; margin-bottom: 2rem; }
    .toc h3 { margin-bottom: 0.5rem; }
    .toc ul { list-style: none; padding-left: 1rem; }
    .toc li { padding: 0.25rem 0; }
    .toc a { color: #1e40af; text-decoration: none; }
    .page-break { page-break-before: always; }
    @media print { .container { padding: 1rem; } .section { break-inside: avoid; } }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>🌿 ecoroadsNZ Analysis Report</h1>
      <p><strong>Project:</strong> ${projectName}</p>
      <p><strong>Generated:</strong> ${currentDate}</p>
      <p><strong>Standards:</strong> NZTA P/17 • EN15804 LCA • NZ ETS</p>
    </div>
    
    <div class="toc">
      <h3>📋 Table of Contents</h3>
      <ul>
        ${
          exportSections.design
            ? '<li><a href="#design">1. Design Parameters</a></li>'
            : ""
        }
        ${
          exportSections.traffic
            ? '<li><a href="#traffic">2. Traffic Analysis</a></li>'
            : ""
        }
        ${
          exportSections.materials
            ? '<li><a href="#materials">3. Materials Selection</a></li>'
            : ""
        }
        ${
          exportSections.biobinder
            ? '<li><a href="#biobinder">4. Biobinder Rheology</a></li>'
            : ""
        }
        ${
          exportSections.performance
            ? '<li><a href="#performance">5. Performance Predictions</a></li>'
            : ""
        }
        ${
          exportSections.carbon
            ? '<li><a href="#carbon">6. Carbon Analysis</a></li>'
            : ""
        }
        ${
          exportSections.lca
            ? '<li><a href="#lca">7. Life Cycle Assessment</a></li>'
            : ""
        }
        ${
          exportSections.economic
            ? '<li><a href="#economic">8. Economic Analysis</a></li>'
            : ""
        }
      </ul>
    </div>

    ${
      exportSections.design
        ? `
    <div class="section" id="design">
      <h2>1. 🌿 Design Parameters</h2>
      <div class="grid">
        <div class="metric">
          <div class="metric-label">Location</div>
          <div class="metric-value">${nzLocation}</div>
          <div class="metric-unit">${climateData.zone}</div>
        </div>
        <div class="metric">
          <div class="metric-label">Road Classification</div>
          <div class="metric-value">${roadStandards.nztaCode}</div>
          <div class="metric-unit">${roadClassification}</div>
        </div>
        <div class="metric">
          <div class="metric-label">Design Life</div>
          <div class="metric-value">${designParams.designLife}</div>
          <div class="metric-unit">years</div>
        </div>
        <div class="metric">
          <div class="metric-label">Reliability Level</div>
          <div class="metric-value">${designParams.reliabilityLevel}%</div>
          <div class="metric-unit">NZTA Standard</div>
        </div>
      </div>
      <h3>Climate Conditions</h3>
      <table>
        <tr><th>Parameter</th><th>Value</th></tr>
        <tr><td>Mean Annual Temperature</td><td>${climateData.meanAnnualTemp}°C</td></tr>
        <tr><td>Annual Rainfall</td><td>${climateData.annualRainfall} mm</td></tr>
        <tr><td>Freeze-Thaw Cycles</td><td>${climateData.freezeThawCycles} per year</td></tr>
        <tr><td>UV Index</td><td>${climateData.uvIndex}</td></tr>
        <tr><td>Seismic Zone</td><td>${climateData.seismicZone}</td></tr>
      </table>
      <h3>Performance Thresholds</h3>
      <table>
        <tr><th>Parameter</th><th>Limit</th></tr>
        <tr><td>Maximum Rutting</td><td>${roadStandards.ruttingLimit} mm</td></tr>
        <tr><td>Maximum Cracking</td><td>${roadStandards.crackingLimit}%</td></tr>
        <tr><td>Terminal Serviceability</td><td>${roadStandards.terminalServiceability}</td></tr>
        <tr><td>Design Speed</td><td>${roadStandards.designSpeed} km/h</td></tr>
      </table>
    </div>
    `
        : ""
    }

    ${
      exportSections.traffic
        ? `
    <div class="section" id="traffic">
      <h2>2. 🚛 Traffic Analysis</h2>
      <div class="grid">
        <div class="metric">
          <div class="metric-label">Initial ADT</div>
          <div class="metric-value">${trafficParams.initialADT.toLocaleString()}</div>
          <div class="metric-unit">vehicles/day</div>
        </div>
        <div class="metric">
          <div class="metric-label">Design ADT</div>
          <div class="metric-value">${trafficAnalysis.designADT.toLocaleString()}</div>
          <div class="metric-unit">vehicles/day</div>
        </div>
        <div class="metric">
          <div class="metric-label">Heavy Vehicles</div>
          <div class="metric-value">${trafficParams.truckPercentage}%</div>
          <div class="metric-unit">${trafficAnalysis.dailyTrucks.toLocaleString()} daily</div>
        </div>
        <div class="metric purple">
          <div class="metric-label">Design ESA</div>
          <div class="metric-value">${(
            trafficAnalysis.designESA / 1000000
          ).toFixed(2)}M</div>
          <div class="metric-unit">Equivalent Standard Axles</div>
        </div>
      </div>
      <h3>Traffic Parameters</h3>
      <table>
        <tr><th>Parameter</th><th>Value</th></tr>
        <tr><td>Growth Rate</td><td>${
          trafficParams.growthRate
        }% per annum</td></tr>
        <tr><td>Direction Factor</td><td>${
          trafficParams.directionFactor
        }</td></tr>
        <tr><td>Lane Factor</td><td>${trafficParams.laneFactor}</td></tr>
      </table>
    </div>
    `
        : ""
    }

    ${
      exportSections.materials
        ? `
    <div class="section" id="materials">
      <h2>3. 🧱 Materials Selection</h2>
      <div class="grid">
        <div class="metric">
          <div class="metric-label">Asphalt Type</div>
          <div class="metric-value">${asphaltType}</div>
          <div class="metric-unit">${materialProps.asphaltThickness} mm thickness</div>
        </div>
        <div class="metric">
          <div class="metric-label">Basecourse Type</div>
          <div class="metric-value">${basecourseType}</div>
          <div class="metric-unit">${materialProps.baseThickness} mm thickness</div>
        </div>
      </div>
      <h3>Asphalt Properties</h3>
      <table>
        <tr><th>Property</th><th>Value</th></tr>
        <tr><td>Modulus</td><td>${selectedAsphalt.modulus} MPa</td></tr>
        <tr><td>Cost</td><td>$${selectedAsphalt.cost}/tonne</td></tr>
        <tr><td>Carbon Intensity</td><td>${selectedAsphalt.carbonIntensity} kgCO₂e/t</td></tr>
      </table>
      <h3>Basecourse Properties</h3>
      <table>
        <tr><th>Property</th><th>Value</th></tr>
        <tr><td>CBR</td><td>${selectedBasecourse.cbr}%</td></tr>
        <tr><td>Cost</td><td>$${selectedBasecourse.cost}/tonne</td></tr>
        <tr><td>Carbon Intensity</td><td>${selectedBasecourse.carbonIntensity} kgCO₂e/t</td></tr>
      </table>
    </div>
    `
        : ""
    }

    ${
      exportSections.biobinder
        ? `
    <div class="section page-break" id="biobinder">
      <h2>4. 🧪 Biobinder Rheology Analysis</h2>
      <div class="info-box ${useBiobinderInAnalysis ? "success" : "warning"}">
        <strong>${
          useBiobinderInAnalysis
            ? "✅ Biobinder ENABLED"
            : "⚠️ Biobinder NOT ENABLED"
        }</strong>
        ${
          useBiobinderInAnalysis
            ? `<br/>Using: ${effectiveBinderProps.name} (${biobinderBlendRatio}% replacement)`
            : "<br/>Standard bitumen C320 is being used in analysis"
        }
      </div>
      <h3>Rheology Comparison</h3>
      <table>
        <tr><th>Property</th><th>Unit</th><th>Standard Bitumen</th><th>${
          useBiobinderInAnalysis
            ? effectiveBinderProps.name
            : "Biobinder (Not Active)"
        }</th></tr>
        <tr><td>Penetration @ 25°C</td><td>dmm</td><td>${
          DEFAULT_BITUMEN.penetration25C
        }</td><td>${currentBiobinder.penetration25C}</td></tr>
        <tr><td>Softening Point</td><td>°C</td><td>${
          DEFAULT_BITUMEN.softeningPoint
        }</td><td>${currentBiobinder.softeningPoint}</td></tr>
        <tr><td>G*/sin(δ)</td><td>kPa</td><td>${
          DEFAULT_BITUMEN.gStar_sinDelta
        }</td><td>${currentBiobinder.gStar_sinDelta}</td></tr>
        <tr><td>m-value (BBR)</td><td>-</td><td>${
          DEFAULT_BITUMEN.mValue
        }</td><td>${currentBiobinder.mValue}</td></tr>
        <tr><td>Stiffness (BBR)</td><td>MPa</td><td>${
          DEFAULT_BITUMEN.stiffness
        }</td><td>${currentBiobinder.stiffness}</td></tr>
        <tr><td>PG Grade High</td><td>°C</td><td>${
          DEFAULT_BITUMEN.pgGradeHigh
        }</td><td>${currentBiobinder.pgGradeHigh}</td></tr>
        <tr><td>PG Grade Low</td><td>°C</td><td>${
          DEFAULT_BITUMEN.pgGradeLow
        }</td><td>${currentBiobinder.pgGradeLow}</td></tr>
        <tr><td>Carbon Intensity</td><td>kgCO₂e/t</td><td>${
          DEFAULT_BITUMEN.carbonIntensity
        }</td><td>${currentBiobinder.carbonIntensity}</td></tr>
      </table>
      ${
        useBiobinderInAnalysis
          ? `
      <h3>Carbon Savings from Biobinder</h3>
      <div class="grid">
        <div class="metric green">
          <div class="metric-label">Carbon Reduction</div>
          <div class="metric-value">${Math.round(
            ((DEFAULT_BITUMEN.carbonIntensity -
              effectiveBinderProps.carbonIntensity) /
              DEFAULT_BITUMEN.carbonIntensity) *
              100
          )}%</div>
          <div class="metric-unit">vs standard bitumen</div>
        </div>
        <div class="metric green">
          <div class="metric-label">Bio Content</div>
          <div class="metric-value">${(
            effectiveBinderProps.bioContent || 0
          ).toFixed(1)}%</div>
          <div class="metric-unit">renewable fraction</div>
        </div>
      </div>
      `
          : ""
      }
    </div>
    `
        : ""
    }

    ${
      exportSections.performance
        ? `
    <div class="section" id="performance">
      <h2>5. 📊 Performance Predictions</h2>
      ${
        performanceResults.rutting.length > 0
          ? `
      <div class="grid">
        <div class="metric ${
          performanceResults.rutting[performanceResults.rutting.length - 1]
            ?.rutting > roadStandards.ruttingLimit
            ? "red"
            : "green"
        }">
          <div class="metric-label">Final Rutting</div>
          <div class="metric-value">${performanceResults.rutting[
            performanceResults.rutting.length - 1
          ]?.rutting.toFixed(1)} mm</div>
          <div class="metric-unit">Limit: ${roadStandards.ruttingLimit} mm</div>
        </div>
        <div class="metric ${
          performanceResults.cracking[performanceResults.cracking.length - 1]
            ?.cracking > roadStandards.crackingLimit
            ? "red"
            : "green"
        }">
          <div class="metric-label">Final Cracking</div>
          <div class="metric-value">${performanceResults.cracking[
            performanceResults.cracking.length - 1
          ]?.cracking.toFixed(1)}%</div>
          <div class="metric-unit">Limit: ${roadStandards.crackingLimit}%</div>
        </div>
        <div class="metric">
          <div class="metric-label">Final IRI</div>
          <div class="metric-value">${performanceResults.roughness[
            performanceResults.roughness.length - 1
          ]?.iri.toFixed(2)}</div>
          <div class="metric-unit">m/km</div>
        </div>
        <div class="metric">
          <div class="metric-label">NAASRA Count</div>
          <div class="metric-value">${performanceResults.roughness[
            performanceResults.roughness.length - 1
          ]?.naasra.toFixed(0)}</div>
          <div class="metric-unit">counts/km</div>
        </div>
      </div>
      ${
        useBiobinderInAnalysis
          ? `
      <div class="info-box info">
        <strong>Biobinder Effect on Performance:</strong>
        <br/>• Rutting factor: ${
          performanceResults.rutting[
            performanceResults.rutting.length - 1
          ]?.binderEffect?.toFixed(3) || 1.0
        }x
        <br/>• Cracking factor: ${
          performanceResults.cracking[
            performanceResults.cracking.length - 1
          ]?.binderEffect?.toFixed(3) || 1.0
        }x
      </div>
      `
          : ""
      }
      <h3>Performance Over Design Life</h3>
      <table>
        <tr><th>Year</th><th>Rutting (mm)</th><th>Cracking (%)</th><th>IRI</th></tr>
        ${[0, 5, 10, 15, 20, 25]
          .filter((y) => y <= designParams.designLife)
          .map((y) => {
            const rut = performanceResults.rutting[y];
            const crack = performanceResults.cracking[y];
            const rough = performanceResults.roughness[y];
            return rut
              ? `<tr><td>${y}</td><td>${rut.rutting.toFixed(
                  2
                )}</td><td>${crack.cracking.toFixed(
                  2
                )}</td><td>${rough.iri.toFixed(2)}</td></tr>`
              : "";
          })
          .join("")}
      </table>
      `
          : '<div class="info-box warning">Performance analysis not yet run. Click "Run Analysis" in the Performance tab.</div>'
      }
    </div>
    `
        : ""
    }

    ${
      exportSections.carbon
        ? `
    <div class="section page-break" id="carbon">
      <h2>6. 🌱 Carbon Analysis</h2>
      <div class="grid">
        <div class="metric red">
          <div class="metric-label">Total Carbon</div>
          <div class="metric-value">${carbonAnalysis.totalCarbon}</div>
          <div class="metric-unit">kgCO₂e per meter</div>
        </div>
        <div class="metric orange">
          <div class="metric-label">Asphalt Carbon</div>
          <div class="metric-value">${carbonAnalysis.asphaltCarbon}</div>
          <div class="metric-unit">kgCO₂e per meter</div>
        </div>
        <div class="metric">
          <div class="metric-label">Basecourse Carbon</div>
          <div class="metric-value">${carbonAnalysis.basecourseCarbon}</div>
          <div class="metric-unit">kgCO₂e per meter</div>
        </div>
        <div class="metric ${useBiobinderInAnalysis ? "green" : ""}">
          <div class="metric-label">Binder Carbon</div>
          <div class="metric-value">${carbonAnalysis.binderCarbon}</div>
          <div class="metric-unit">kgCO₂e per meter</div>
        </div>
      </div>
      ${
        useBiobinderInAnalysis
          ? `
      <div class="info-box success">
        <strong>Biobinder Carbon Savings:</strong> ${carbonAnalysis.binderCarbonSavings} kgCO₂e/m (${carbonAnalysis.binderCarbonSavingsPercent}% reduction)
      </div>
      `
          : ""
      }
      <h3>ETS Cost Projections</h3>
      <table>
        <tr><th>Year</th><th>Carbon Price ($/t)</th><th>Cost per meter</th></tr>
        <tr><td>2025</td><td>$${
          carbonMechanisms.ets.forecast[2025]
        }</td><td>$${(
            (parseFloat(carbonAnalysis.totalCarbon) *
              carbonMechanisms.ets.forecast[2025]) /
            1000
          ).toFixed(2)}</td></tr>
        <tr><td>2030</td><td>$${
          carbonMechanisms.ets.forecast[2030]
        }</td><td>$${(
            (parseFloat(carbonAnalysis.totalCarbon) *
              carbonMechanisms.ets.forecast[2030]) /
            1000
          ).toFixed(2)}</td></tr>
        <tr><td>2035</td><td>$${
          carbonMechanisms.ets.forecast[2035]
        }</td><td>$${(
            (parseFloat(carbonAnalysis.totalCarbon) *
              carbonMechanisms.ets.forecast[2035]) /
            1000
          ).toFixed(2)}</td></tr>
      </table>
    </div>
    `
        : ""
    }

    ${
      exportSections.lca
        ? `
    <div class="section" id="lca">
      <h2>7. ♻️ Life Cycle Assessment (EN15804)</h2>
      <div class="grid">
        <div class="metric red">
          <div class="metric-label">Total Emissions</div>
          <div class="metric-value">${Math.round(
            lcaTotalEmissions
          ).toLocaleString()}</div>
          <div class="metric-unit">kgCO₂e</div>
        </div>
        <div class="metric orange">
          <div class="metric-label">Scope 1</div>
          <div class="metric-value">${Math.round(
            lcaBreakdown.scope1
          ).toLocaleString()}</div>
          <div class="metric-unit">kgCO₂e (Direct)</div>
        </div>
        <div class="metric">
          <div class="metric-label">Scope 2</div>
          <div class="metric-value">${Math.round(
            lcaBreakdown.scope2
          ).toLocaleString()}</div>
          <div class="metric-unit">kgCO₂e (Energy)</div>
        </div>
        <div class="metric purple">
          <div class="metric-label">Scope 3</div>
          <div class="metric-value">${Math.round(
            lcaBreakdown.scope3
          ).toLocaleString()}</div>
          <div class="metric-unit">kgCO₂e (Indirect)</div>
        </div>
      </div>
      <h3>LCA Entries by Module</h3>
      <table>
        <tr><th>Module</th><th>Process</th><th>Mass (kg)</th><th>EF</th><th>Total (kgCO₂e)</th><th>Scope</th><th>Source</th></tr>
        ${allLcaEntries
          .map(
            (e) => `
        <tr>
          <td><span class="badge badge-blue">${e.module}</span></td>
          <td>${e.title}</td>
          <td>${Math.round(e.mass).toLocaleString()}</td>
          <td>${typeof e.ef === "number" ? e.ef.toFixed(1) : e.ef}</td>
          <td><strong>${Math.round(e.total).toLocaleString()}</strong></td>
          <td><span class="badge ${
            e.scope === "1"
              ? "badge-red"
              : e.scope === "2"
              ? "badge-orange"
              : "badge-purple"
          }">S${e.scope}</span></td>
          <td>${e.isAuto ? "🔗 Auto" : "✏️ Manual"}</td>
        </tr>
        `
          )
          .join("")}
      </table>
    </div>
    `
        : ""
    }

    ${
      exportSections.economic
        ? `
    <div class="section page-break" id="economic">
      <h2>8. 💰 Economic Analysis</h2>
      <h3>Project Parameters</h3>
      <table>
        <tr><th>Parameter</th><th>Value</th></tr>
        <tr><td>Initial Investment</td><td>$${economicInputs.initialInvestment.toLocaleString()}</td></tr>
        <tr><td>Project Lifespan</td><td>${
          economicInputs.projectLifespan
        } years</td></tr>
        <tr><td>Discount Rate</td><td>${economicInputs.discountRate}%</td></tr>
        <tr><td>Annual Savings</td><td>$${economicInputs.annualSavings.toLocaleString()}</td></tr>
        <tr><td>Annual Operating Cost</td><td>$${economicInputs.annualOperatingCost.toLocaleString()}</td></tr>
      </table>
      
      <h3>Carbon Pricing Mechanisms</h3>
      <table>
        <tr><th>Mechanism</th><th>2025</th><th>2030</th><th>2035</th><th>2040</th><th>2050</th><th>Compliance</th></tr>
        <tr><td>📊 ETS</td><td>$${
          carbonMechanisms.ets.forecast[2025]
        }</td><td>$${carbonMechanisms.ets.forecast[2030]}</td><td>$${
            carbonMechanisms.ets.forecast[2035]
          }</td><td>$${carbonMechanisms.ets.forecast[2040]}</td><td>$${
            carbonMechanisms.ets.forecast[2050]
          }</td><td>${carbonMechanisms.ets.complianceCost}%</td></tr>
        <tr><td>🏛️ Carbon Tax</td><td>$${
          carbonMechanisms.carbonTax.forecast[2025]
        }</td><td>$${carbonMechanisms.carbonTax.forecast[2030]}</td><td>$${
            carbonMechanisms.carbonTax.forecast[2035]
          }</td><td>$${carbonMechanisms.carbonTax.forecast[2040]}</td><td>$${
            carbonMechanisms.carbonTax.forecast[2050]
          }</td><td>${carbonMechanisms.carbonTax.complianceCost}%</td></tr>
        <tr><td>⚖️ Hybrid</td><td>$${
          carbonMechanisms.hybrid.forecast[2025]
        }</td><td>$${carbonMechanisms.hybrid.forecast[2030]}</td><td>$${
            carbonMechanisms.hybrid.forecast[2035]
          }</td><td>$${carbonMechanisms.hybrid.forecast[2040]}</td><td>$${
            carbonMechanisms.hybrid.forecast[2050]
          }</td><td>${carbonMechanisms.hybrid.complianceCost}%</td></tr>
      </table>
      
      <h3>Carbon Cost Comparison</h3>
      <table>
        <tr><th>Mechanism</th><th>Total Carbon Cost</th><th>Average Annual</th></tr>
        ${economicAnalysis.mechanismComparison
          .map(
            (m) => `
        <tr ${m.key === carbonMechanism ? 'style="background: #ecfdf5;"' : ""}>
          <td>${
            m.key === "ets"
              ? "📊 ETS"
              : m.key === "carbonTax"
              ? "🏛️ Carbon Tax"
              : "⚖️ Hybrid"
          }${
              m.key === carbonMechanism
                ? ' <span class="badge badge-green">Selected</span>'
                : ""
            }</td>
          <td>$${Math.round(m.totalCost).toLocaleString()}</td>
          <td>$${Math.round(m.avgAnnual).toLocaleString()}/year</td>
        </tr>
        `
          )
          .join("")}
      </table>
      
      <h3>Analysis Results (${
        carbonMechanism === "ets"
          ? "ETS"
          : carbonMechanism === "carbonTax"
          ? "Carbon Tax"
          : "Hybrid"
      })</h3>
      <div class="grid">
        <div class="metric ${economicAnalysis.npv >= 0 ? "green" : "red"}">
          <div class="metric-label">Net Present Value</div>
          <div class="metric-value">$${Math.round(
            economicAnalysis.npv
          ).toLocaleString()}</div>
          <div class="metric-unit">${
            economicAnalysis.npv >= 0 ? "Viable" : "Not Viable"
          }</div>
        </div>
        <div class="metric ${
          economicAnalysis.irr >= economicInputs.discountRate
            ? "green"
            : "orange"
        }">
          <div class="metric-label">Internal Rate of Return</div>
          <div class="metric-value">${economicAnalysis.irr.toFixed(1)}%</div>
          <div class="metric-unit">Target: ${economicInputs.discountRate}%</div>
        </div>
        <div class="metric">
          <div class="metric-label">Payback Period</div>
          <div class="metric-value">${economicAnalysis.paybackPeriod}</div>
          <div class="metric-unit">years</div>
        </div>
        <div class="metric ${economicAnalysis.bcr >= 1 ? "green" : "red"}">
          <div class="metric-label">Benefit-Cost Ratio</div>
          <div class="metric-value">${economicAnalysis.bcr.toFixed(2)}</div>
          <div class="metric-unit">${
            economicAnalysis.bcr >= 1 ? "Favorable" : "Unfavorable"
          }</div>
        </div>
      </div>
      
      <h3>Carbon Cost Breakdown</h3>
      <div class="grid">
        <div class="metric red">
          <div class="metric-label">Total Carbon Cost</div>
          <div class="metric-value">$${Math.round(
            economicAnalysis.totalCarbonCost
          ).toLocaleString()}</div>
          <div class="metric-unit">over project life</div>
        </div>
        <div class="metric orange">
          <div class="metric-label">NPV of Carbon Cost</div>
          <div class="metric-value">$${Math.round(
            economicAnalysis.totalDiscountedCarbonCost
          ).toLocaleString()}</div>
          <div class="metric-unit">discounted</div>
        </div>
      </div>
    </div>
    `
        : ""
    }

    <div class="footer">
      <p>Generated by <strong>ecoroadsNZ</strong> - Advanced Pavement Design & Carbon Analytics</p>
      <p>NZTA Standards • EN15804 LCA • NZ ETS Integration</p>
      <p>${currentDate}</p>
    </div>
  </div>
</body>
</html>
    `;

    // Create and download the file
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ecoroadsNZ_Report_${nzLocation}_${
      new Date().toISOString().split("T")[0]
    }.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExportDialog(false);
  };

  const navItems = [
    { id: "design", label: "Design", icon: "🌿" },
    { id: "traffic", label: "Traffic", icon: "🚛" },
    { id: "materials", label: "Materials", icon: "🧱" },
    { id: "biobinder", label: "Biobinder Rheology", icon: "🧪" },
    { id: "performance", label: "Performance", icon: "📊" },
    { id: "carbon", label: "Carbon", icon: "🌱" },
    { id: "lca", label: "LCA", icon: "♻️" },
    { id: "economic", label: "Economic", icon: "💰" },
  ];

  // ============================================================================
  // BIOBINDER TAB
  // ============================================================================
  const BiobinderTab = () => {
    const currentBio = selectedBiobinder
      ? BIOBINDER_CANDIDATES.find((b) => b.id === selectedBiobinder) ||
        customBiobinder
      : customBiobinder;
    const comparisonData = [
      {
        property: "Penetration @ 25°C",
        unit: "dmm",
        bitumen: DEFAULT_BITUMEN.penetration25C,
        biobinder: currentBio.penetration25C,
      },
      {
        property: "Softening Point",
        unit: "°C",
        bitumen: DEFAULT_BITUMEN.softeningPoint,
        biobinder: currentBio.softeningPoint,
      },
      {
        property: "Viscosity @ 135°C",
        unit: "cP",
        bitumen: DEFAULT_BITUMEN.viscosity135C,
        biobinder: currentBio.viscosity135C,
      },
      {
        property: "G* @ 25°C",
        unit: "Pa",
        bitumen: DEFAULT_BITUMEN.gStar25C,
        biobinder: currentBio.gStar25C,
      },
      {
        property: "G*/sin(δ)",
        unit: "kPa",
        bitumen: DEFAULT_BITUMEN.gStar_sinDelta,
        biobinder: currentBio.gStar_sinDelta,
      },
      {
        property: "m-value (BBR)",
        unit: "-",
        bitumen: DEFAULT_BITUMEN.mValue,
        biobinder: currentBio.mValue,
      },
      {
        property: "Stiffness (BBR)",
        unit: "MPa",
        bitumen: DEFAULT_BITUMEN.stiffness,
        biobinder: currentBio.stiffness,
      },
      {
        property: "Flash Point",
        unit: "°C",
        bitumen: DEFAULT_BITUMEN.flashPoint,
        biobinder: currentBio.flashPoint,
      },
      {
        property: "PG Grade High",
        unit: "°C",
        bitumen: DEFAULT_BITUMEN.pgGradeHigh,
        biobinder: currentBio.pgGradeHigh,
      },
      {
        property: "PG Grade Low",
        unit: "°C",
        bitumen: DEFAULT_BITUMEN.pgGradeLow,
        biobinder: currentBio.pgGradeLow,
      },
      {
        property: "Carbon Intensity",
        unit: "kgCO₂e/t",
        bitumen: DEFAULT_BITUMEN.carbonIntensity,
        biobinder: currentBio.carbonIntensity,
      },
    ];

    return (
      <div>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <h2 style={S.sectionTitle}>🧪 Biobinder Rheology Properties</h2>
          <p style={S.sectionSub}>
            Configure and compare bio-based binder candidates against standard
            bitumen
          </p>
        </div>

        {/* INTEGRATION TOGGLE - KEY NEW FEATURE */}
        <div
          style={{
            ...S.card,
            marginBottom: "1.5rem",
            background: useBiobinderInAnalysis
              ? "linear-gradient(135deg, #dcfce7, #d1fae5)"
              : "linear-gradient(135deg, #fef3c7, #fef9c3)",
            border: useBiobinderInAnalysis
              ? "2px solid #059669"
              : "2px solid #d97706",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "1rem",
            }}
          >
            <div>
              <h3
                style={{
                  ...S.cardTitle,
                  marginBottom: "0.25rem",
                  color: useBiobinderInAnalysis ? "#065f46" : "#92400e",
                }}
              >
                {useBiobinderInAnalysis
                  ? "✅ Biobinder ENABLED in Analysis"
                  : "⚠️ Biobinder NOT Used in Analysis"}
              </h3>
              <p
                style={{
                  fontSize: "0.875rem",
                  color: useBiobinderInAnalysis ? "#047857" : "#a16207",
                  margin: 0,
                }}
              >
                {useBiobinderInAnalysis
                  ? `Using ${effectiveBinderProps.name} in Performance, Carbon, LCA & Economic calculations`
                  : "Toggle ON to include biobinder properties in all analysis tabs"}
              </p>
            </div>
            <button
              onClick={() => setUseBiobinderInAnalysis(!useBiobinderInAnalysis)}
              style={{
                ...S.btnPrimary,
                background: useBiobinderInAnalysis
                  ? "#dc2626"
                  : "linear-gradient(135deg, #059669, #10b981)",
                minWidth: "180px",
              }}
            >
              {useBiobinderInAnalysis
                ? "🔴 Disable Biobinder"
                : "🟢 Enable Biobinder"}
            </button>
          </div>

          {/* Blend Ratio Slider */}
          {useBiobinderInAnalysis && (
            <div
              style={{
                marginTop: "1.5rem",
                padding: "1rem",
                background: "rgba(255,255,255,0.7)",
                borderRadius: "0.75rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.5rem",
                }}
              >
                <label style={{ fontWeight: "600", color: "#065f46" }}>
                  Biobinder Replacement Ratio
                </label>
                <span
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    color: "#059669",
                  }}
                >
                  {biobinderBlendRatio}%
                </span>
              </div>
              <input
                type="range"
                min="10"
                max="100"
                step="5"
                value={biobinderBlendRatio}
                onChange={(e) => setBiobinderBlendRatio(Number(e.target.value))}
                style={{ width: "100%", height: "8px", cursor: "pointer" }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "0.75rem",
                  color: "#6b7280",
                  marginTop: "0.25rem",
                }}
              >
                <span>10% (Light blend)</span>
                <span>50% (Moderate)</span>
                <span>100% (Full replacement)</span>
              </div>
              <div
                style={{
                  marginTop: "1rem",
                  fontSize: "0.8rem",
                  color: "#047857",
                }}
              >
                <strong>Effective Binder:</strong> {biobinderBlendRatio}%{" "}
                {currentBio.name} + {100 - biobinderBlendRatio}% Standard
                Bitumen
              </div>
            </div>
          )}
        </div>

        {/* Impact Summary when enabled */}
        {useBiobinderInAnalysis && (
          <div
            style={{ ...S.card, marginBottom: "1.5rem", background: "#f0fdf4" }}
          >
            <h3 style={S.cardTitle}>📊 Biobinder Impact on Analysis</h3>
            <div style={S.grid4}>
              <div
                style={{
                  padding: "1rem",
                  background: "white",
                  borderRadius: "0.75rem",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                  Performance Tab
                </div>
                <div
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                    color: "#059669",
                  }}
                >
                  🔗 Linked
                </div>
                <div style={{ fontSize: "0.7rem", color: "#047857" }}>
                  Rheology affects rutting, cracking
                </div>
              </div>
              <div
                style={{
                  padding: "1rem",
                  background: "white",
                  borderRadius: "0.75rem",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                  Carbon Tab
                </div>
                <div
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                    color: "#059669",
                  }}
                >
                  🔗 Linked
                </div>
                <div style={{ fontSize: "0.7rem", color: "#047857" }}>
                  -
                  {Math.round(
                    ((DEFAULT_BITUMEN.carbonIntensity -
                      effectiveBinderProps.carbonIntensity) /
                      DEFAULT_BITUMEN.carbonIntensity) *
                      100
                  )}
                  % binder carbon
                </div>
              </div>
              <div
                style={{
                  padding: "1rem",
                  background: "white",
                  borderRadius: "0.75rem",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                  LCA Tab
                </div>
                <div
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                    color: "#059669",
                  }}
                >
                  🔗 Linked
                </div>
                <div style={{ fontSize: "0.7rem", color: "#047857" }}>
                  Auto-entries use biobinder EF
                </div>
              </div>
              <div
                style={{
                  padding: "1rem",
                  background: "white",
                  borderRadius: "0.75rem",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                  Economic Tab
                </div>
                <div
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                    color: "#059669",
                  }}
                >
                  🔗 Linked
                </div>
                <div style={{ fontSize: "0.7rem", color: "#047857" }}>
                  Lower carbon = lower cost
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ ...S.infoBox("info"), marginBottom: "1.5rem" }}>
          <strong>📋 Reference: Standard Bitumen C320</strong>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "0.5rem",
              marginTop: "0.5rem",
              fontSize: "0.8rem",
            }}
          >
            <span>• Penetration: {DEFAULT_BITUMEN.penetration25C} dmm</span>
            <span>• Softening Point: {DEFAULT_BITUMEN.softeningPoint}°C</span>
            <span>• G*/sin(δ): {DEFAULT_BITUMEN.gStar_sinDelta} kPa</span>
            <span>
              • PG: {DEFAULT_BITUMEN.pgGradeHigh}/{DEFAULT_BITUMEN.pgGradeLow}
            </span>
            <span>• m-value: {DEFAULT_BITUMEN.mValue}</span>
            <span>• Carbon: {DEFAULT_BITUMEN.carbonIntensity} kgCO₂e/t</span>
          </div>
        </div>

        <div style={S.grid2}>
          <div style={S.card}>
            <h3 style={S.cardTitle}>🌱 Select Biobinder Candidate</h3>
            <SelectField
              label="Pre-defined Options"
              value={selectedBiobinder || ""}
              onChange={(v) => setSelectedBiobinder(v || null)}
              options={[
                { value: "", label: "Custom Biobinder (Edit Below)" },
                ...BIOBINDER_CANDIDATES.map((b) => ({
                  value: b.id,
                  label: `${b.name} (${b.bioContent}% bio)`,
                })),
              ]}
            />
            {selectedBiobinder && (
              <div style={{ ...S.infoBox("success"), marginTop: "1rem" }}>
                <strong>
                  {
                    BIOBINDER_CANDIDATES.find((b) => b.id === selectedBiobinder)
                      ?.name
                  }
                </strong>
                <br />
                <span style={{ fontSize: "0.8rem" }}>
                  Source:{" "}
                  {
                    BIOBINDER_CANDIDATES.find((b) => b.id === selectedBiobinder)
                      ?.source
                  }
                </span>
              </div>
            )}
            <div style={{ ...S.grid2, marginTop: "1rem" }}>
              <MetricCard
                title="Bio Content"
                value={`${currentBio.bioContent || 0}%`}
                unit="Renewable"
                color="green"
                icon="🌿"
              />
              <MetricCard
                title="Carbon Savings"
                value={`${Math.round(
                  ((DEFAULT_BITUMEN.carbonIntensity -
                    currentBio.carbonIntensity) /
                    DEFAULT_BITUMEN.carbonIntensity) *
                    100
                )}%`}
                unit="vs Standard"
                color="green"
                icon="📉"
              />
            </div>
          </div>

          <div style={S.card}>
            <h3 style={S.cardTitle}>⚙️ Custom Biobinder Properties</h3>
            <p
              style={{
                fontSize: "0.8rem",
                color: "#6b7280",
                marginBottom: "1rem",
              }}
            >
              Edit rheological properties for custom formulations
            </p>
            <div style={{ maxHeight: "350px", overflowY: "auto" }}>
              <InputField
                label="Name"
                value={customBiobinder.name}
                onChange={(v) => setCustomBiobinder((p) => ({ ...p, name: v }))}
                type="text"
              />
              <InputField
                label="Penetration @ 25°C"
                value={customBiobinder.penetration25C}
                onChange={(v) =>
                  setCustomBiobinder((p) => ({ ...p, penetration25C: v }))
                }
                unit="dmm"
                helpText={`Default: ${DEFAULT_BITUMEN.penetration25C}`}
              />
              <InputField
                label="Softening Point"
                value={customBiobinder.softeningPoint}
                onChange={(v) =>
                  setCustomBiobinder((p) => ({ ...p, softeningPoint: v }))
                }
                unit="°C"
                helpText={`Default: ${DEFAULT_BITUMEN.softeningPoint}`}
              />
              <InputField
                label="G*/sin(δ)"
                value={customBiobinder.gStar_sinDelta}
                onChange={(v) =>
                  setCustomBiobinder((p) => ({ ...p, gStar_sinDelta: v }))
                }
                unit="kPa"
                step={0.1}
                helpText={`Default: ${DEFAULT_BITUMEN.gStar_sinDelta} (min 1.0 SHRP)`}
              />
              <InputField
                label="m-value (BBR)"
                value={customBiobinder.mValue}
                onChange={(v) =>
                  setCustomBiobinder((p) => ({ ...p, mValue: v }))
                }
                unit="-"
                step={0.01}
                helpText={`Default: ${DEFAULT_BITUMEN.mValue} (min 0.30 SHRP)`}
              />
              <InputField
                label="Stiffness (BBR)"
                value={customBiobinder.stiffness}
                onChange={(v) =>
                  setCustomBiobinder((p) => ({ ...p, stiffness: v }))
                }
                unit="MPa"
                helpText={`Default: ${DEFAULT_BITUMEN.stiffness} (max 300 SHRP)`}
              />
              <InputField
                label="Flash Point"
                value={customBiobinder.flashPoint}
                onChange={(v) =>
                  setCustomBiobinder((p) => ({ ...p, flashPoint: v }))
                }
                unit="°C"
                helpText={`Default: ${DEFAULT_BITUMEN.flashPoint}`}
              />
              <InputField
                label="PG Grade High"
                value={customBiobinder.pgGradeHigh}
                onChange={(v) =>
                  setCustomBiobinder((p) => ({ ...p, pgGradeHigh: v }))
                }
                unit="°C"
              />
              <InputField
                label="PG Grade Low"
                value={customBiobinder.pgGradeLow}
                onChange={(v) =>
                  setCustomBiobinder((p) => ({ ...p, pgGradeLow: v }))
                }
                unit="°C"
              />
              <InputField
                label="Carbon Intensity"
                value={customBiobinder.carbonIntensity}
                onChange={(v) =>
                  setCustomBiobinder((p) => ({ ...p, carbonIntensity: v }))
                }
                unit="kgCO₂e/t"
                helpText={`Default: ${DEFAULT_BITUMEN.carbonIntensity}`}
              />
              <InputField
                label="Bio-content"
                value={customBiobinder.bioContent}
                onChange={(v) =>
                  setCustomBiobinder((p) => ({ ...p, bioContent: v }))
                }
                unit="%"
                helpText="Renewable fraction"
              />
            </div>
          </div>
        </div>

        <div style={{ ...S.card, marginTop: "1.5rem" }}>
          <h3 style={S.cardTitle}>📊 Rheology Comparison Table</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Property</th>
                  <th style={S.th}>Unit</th>
                  <th style={{ ...S.th, background: "#fef3c7" }}>
                    Standard Bitumen
                  </th>
                  <th style={{ ...S.th, background: "#dcfce7" }}>
                    {currentBio.name}
                  </th>
                  <th style={S.th}>Difference</th>
                  <th style={S.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => {
                  const diff = (
                    ((row.biobinder - row.bitumen) / row.bitumen) *
                    100
                  ).toFixed(1);
                  const isGood = row.property.includes("Carbon")
                    ? row.biobinder < row.bitumen
                    : Math.abs(parseFloat(diff)) < 20;
                  return (
                    <tr key={idx}>
                      <td style={{ ...S.td, fontWeight: "500" }}>
                        {row.property}
                      </td>
                      <td style={S.td}>{row.unit}</td>
                      <td style={{ ...S.td, background: "#fffbeb" }}>
                        {row.bitumen}
                      </td>
                      <td style={{ ...S.td, background: "#ecfdf5" }}>
                        {row.biobinder}
                      </td>
                      <td style={S.td}>
                        <span
                          style={{
                            color:
                              parseFloat(diff) >= 0 ? "#059669" : "#dc2626",
                          }}
                        >
                          {parseFloat(diff) >= 0 ? "+" : ""}
                          {diff}%
                        </span>
                      </td>
                      <td style={S.td}>
                        <span style={S.badge(isGood ? "green" : "orange")}>
                          {isGood ? "✓ OK" : "⚠ Review"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ ...S.card, marginTop: "1.5rem" }}>
          <h3 style={S.cardTitle}>✅ SHRP/Superpave Compliance Check</h3>
          <div style={S.grid4}>
            {[
              {
                label: "G*/sin(δ) ≥ 1.0 kPa",
                value: currentBio.gStar_sinDelta,
                check: currentBio.gStar_sinDelta >= 1.0,
              },
              {
                label: "m-value ≥ 0.30",
                value: currentBio.mValue,
                check: currentBio.mValue >= 0.3,
              },
              {
                label: "Stiffness ≤ 300 MPa",
                value: currentBio.stiffness,
                check: currentBio.stiffness <= 300,
              },
              {
                label: "Flash Point ≥ 230°C",
                value: currentBio.flashPoint,
                check: currentBio.flashPoint >= 230,
              },
            ].map((item, idx) => (
              <div
                key={idx}
                style={{
                  padding: "1rem",
                  borderRadius: "0.75rem",
                  background: item.check ? "#dcfce7" : "#fee2e2",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#374151" }}>
                  {item.label}
                </div>
                <div
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    color: item.check ? "#059669" : "#dc2626",
                  }}
                >
                  {item.check ? "✓ PASS" : "✗ FAIL"}
                </div>
                <div style={{ fontSize: "0.75rem" }}>Actual: {item.value}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...S.grid2, marginTop: "1.5rem" }}>
          <div style={S.card}>
            <h3 style={S.cardTitle}>🌡️ Performance Grade</h3>
            <div style={S.grid2}>
              <div
                style={{
                  textAlign: "center",
                  padding: "1rem",
                  background: "#fffbeb",
                  borderRadius: "0.75rem",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#92400e" }}>
                  Standard Bitumen
                </div>
                <div
                  style={{
                    fontSize: "2rem",
                    fontWeight: "bold",
                    color: "#d97706",
                  }}
                >
                  PG {DEFAULT_BITUMEN.pgGradeHigh}-
                  {Math.abs(DEFAULT_BITUMEN.pgGradeLow)}
                </div>
              </div>
              <div
                style={{
                  textAlign: "center",
                  padding: "1rem",
                  background: "#ecfdf5",
                  borderRadius: "0.75rem",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#065f46" }}>
                  Biobinder
                </div>
                <div
                  style={{
                    fontSize: "2rem",
                    fontWeight: "bold",
                    color: "#059669",
                  }}
                >
                  PG {currentBio.pgGradeHigh}-{Math.abs(currentBio.pgGradeLow)}
                </div>
              </div>
            </div>
          </div>
          <div style={S.card}>
            <h3 style={S.cardTitle}>🌱 Carbon Savings</h3>
            <div
              style={{
                padding: "1.5rem",
                background: "linear-gradient(135deg, #dcfce7, #d1fae5)",
                borderRadius: "0.75rem",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "0.875rem", color: "#065f46" }}>
                {DEFAULT_BITUMEN.carbonIntensity} → {currentBio.carbonIntensity}{" "}
                kgCO₂e/tonne
              </div>
              <div
                style={{
                  fontSize: "3rem",
                  fontWeight: "bold",
                  color: "#059669",
                }}
              >
                -
                {Math.round(
                  ((DEFAULT_BITUMEN.carbonIntensity -
                    currentBio.carbonIntensity) /
                    DEFAULT_BITUMEN.carbonIntensity) *
                    100
                )}
                %
              </div>
              <div style={{ fontSize: "0.75rem", color: "#047857" }}>
                Carbon Intensity Reduction
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Other Tabs
  const DesignTab = () => (
    <div>
      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <h2 style={S.sectionTitle}>🌿 NZ Design Standards</h2>
        <p style={S.sectionSub}>
          Configure design parameters based on NZTA standards
        </p>
      </div>
      <div style={S.grid2}>
        <div style={S.card}>
          <h3 style={S.cardTitle}>📍 Location</h3>
          <SelectField
            label="Location"
            value={nzLocation}
            onChange={setNzLocation}
            options={Object.keys(NZ_CLIMATE_ZONES)}
          />
          <div
            style={{
              marginTop: "1rem",
              fontSize: "0.875rem",
              color: "#6b7280",
            }}
          >
            <div>
              <strong>Zone:</strong> {climateData.zone}
            </div>
            <div>
              <strong>Temp:</strong> {climateData.meanAnnualTemp}°C
            </div>
            <div>
              <strong>Rainfall:</strong> {climateData.annualRainfall}mm
            </div>
            <div>
              <strong>Seismic:</strong> {climateData.seismicZone}
            </div>
          </div>
        </div>
        <div style={S.card}>
          <h3 style={S.cardTitle}>🛣️ Road Class</h3>
          <SelectField
            label="NZTA Class"
            value={roadClassification}
            onChange={setRoadClassification}
            options={Object.keys(NZ_ROAD_CLASSIFICATIONS)}
          />
          <div
            style={{
              marginTop: "1rem",
              fontSize: "0.875rem",
              color: "#6b7280",
            }}
          >
            <div>
              <strong>Design Life:</strong> {roadStandards.designLife} yrs
            </div>
            <div>
              <strong>Reliability:</strong> {roadStandards.reliabilityLevel}%
            </div>
            <div>
              <strong>Code:</strong> {roadStandards.nztaCode}
            </div>
          </div>
        </div>
      </div>
      <div style={{ ...S.card, marginTop: "1.5rem" }}>
        <h3 style={S.cardTitle}>📏 Thresholds</h3>
        <div style={S.grid4}>
          <MetricCard
            title="Rutting"
            value={`${roadStandards.ruttingLimit} mm`}
            color="red"
            icon="📉"
          />
          <MetricCard
            title="Cracking"
            value={`${roadStandards.crackingLimit}%`}
            color="purple"
            icon="🔀"
          />
          <MetricCard
            title="Terminal PSI"
            value={roadStandards.terminalServiceability.toFixed(1)}
            color="green"
            icon="📏"
          />
          <MetricCard
            title="Speed"
            value={`${roadStandards.designSpeed} km/h`}
            color="blue"
            icon="🚗"
          />
        </div>
      </div>
    </div>
  );

  const TrafficTab = () => (
    <div>
      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <h2 style={S.sectionTitle}>🚛 Traffic Analysis</h2>
        <p style={S.sectionSub}>Configure traffic parameters</p>
      </div>
      <div style={S.grid2}>
        <div style={S.card}>
          <h3 style={S.cardTitle}>🚛 Parameters</h3>
          <InputField
            label="Initial ADT"
            value={trafficParams.initialADT}
            onChange={(v) => setTrafficParams((p) => ({ ...p, initialADT: v }))}
            unit="vpd"
          />
          <InputField
            label="Heavy Vehicle %"
            value={trafficParams.truckPercentage}
            onChange={(v) =>
              setTrafficParams((p) => ({ ...p, truckPercentage: v }))
            }
            unit="%"
          />
          <InputField
            label="Growth Rate"
            value={trafficParams.growthRate}
            onChange={(v) => setTrafficParams((p) => ({ ...p, growthRate: v }))}
            unit="% p.a."
            step={0.1}
            helpText={`Regional: ${regionalGrowthRate}%`}
          />
        </div>
        <div style={S.card}>
          <h3 style={S.cardTitle}>📊 ESA</h3>
          <div style={S.grid2}>
            <MetricCard
              title="Design ADT"
              value={trafficAnalysis.designADT.toLocaleString()}
              color="blue"
            />
            <MetricCard
              title="Daily HV"
              value={trafficAnalysis.dailyTrucks.toLocaleString()}
              color="orange"
            />
          </div>
          <div
            style={{
              marginTop: "1rem",
              padding: "1rem",
              background: "linear-gradient(135deg, #dbeafe, #e0e7ff)",
              borderRadius: "0.75rem",
            }}
          >
            <div style={{ fontSize: "0.875rem", color: "#1e40af" }}>
              Total Design ESA
            </div>
            <div
              style={{ fontSize: "2rem", fontWeight: "bold", color: "#1e40af" }}
            >
              {(trafficAnalysis.designESA / 1000000).toFixed(2)}M
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const MaterialsTab = () => (
    <div>
      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <h2 style={S.sectionTitle}>🧱 Materials</h2>
        <p style={S.sectionSub}>Select local NZ materials</p>
      </div>
      <div style={S.grid2}>
        <div style={S.card}>
          <h3 style={S.cardTitle}>🏗️ Asphalt</h3>
          <SelectField
            label="Type"
            value={asphaltType}
            onChange={setAsphaltType}
            options={Object.keys(NZ_MATERIALS.asphalt)}
          />
          <InputField
            label="Thickness"
            value={materialProps.asphaltThickness}
            onChange={(v) =>
              setMaterialProps((p) => ({ ...p, asphaltThickness: v }))
            }
            unit="mm"
          />
          <div
            style={{
              marginTop: "1rem",
              fontSize: "0.875rem",
              color: "#6b7280",
            }}
          >
            <div>
              <strong>Modulus:</strong> {selectedAsphalt.modulus} MPa
            </div>
            <div>
              <strong>Cost:</strong> ${selectedAsphalt.cost}/t
            </div>
            <div>
              <strong>Carbon:</strong> {selectedAsphalt.carbonIntensity}{" "}
              kgCO₂e/t
            </div>
          </div>
        </div>
        <div style={S.card}>
          <h3 style={S.cardTitle}>🪨 Basecourse</h3>
          <SelectField
            label="Type"
            value={basecourseType}
            onChange={setBasecourseType}
            options={Object.keys(NZ_MATERIALS.basecourse)}
          />
          <InputField
            label="Thickness"
            value={materialProps.baseThickness}
            onChange={(v) =>
              setMaterialProps((p) => ({ ...p, baseThickness: v }))
            }
            unit="mm"
          />
          <div
            style={{
              marginTop: "1rem",
              fontSize: "0.875rem",
              color: "#6b7280",
            }}
          >
            <div>
              <strong>CBR:</strong> {selectedBasecourse.cbr}%
            </div>
            <div>
              <strong>Cost:</strong> ${selectedBasecourse.cost}/t
            </div>
            <div>
              <strong>Carbon:</strong> {selectedBasecourse.carbonIntensity}{" "}
              kgCO₂e/t
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const PerformanceTab = () => (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "2rem",
        }}
      >
        <div>
          <h2 style={S.sectionTitle}>📊 Performance</h2>
          <p style={S.sectionSub}>
            Run Austroads analysis -{" "}
            {useBiobinderInAnalysis
              ? `With ${effectiveBinderProps.name}`
              : "Standard Bitumen"}
          </p>
        </div>
        <button style={S.btnPrimary} onClick={runNZAnalysis}>
          🔄 Run Analysis
        </button>
      </div>

      {/* Biobinder Status */}
      {useBiobinderInAnalysis && (
        <div style={{ ...S.infoBox("info"), marginBottom: "1.5rem" }}>
          <strong>🧪 Biobinder Rheology Effects Applied:</strong>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "0.5rem",
              marginTop: "0.5rem",
              fontSize: "0.8rem",
            }}
          >
            <span>
              • G*/sin(δ): {effectiveBinderProps.gStar_sinDelta.toFixed(2)} kPa
              → Affects rutting
            </span>
            <span>
              • m-value: {effectiveBinderProps.mValue.toFixed(2)} → Affects
              cracking
            </span>
            <span>
              • Softening Point:{" "}
              {effectiveBinderProps.softeningPoint.toFixed(1)}°C → Affects
              high-temp performance
            </span>
            <span>
              • PG Grade: {effectiveBinderProps.pgGradeHigh}-
              {Math.abs(effectiveBinderProps.pgGradeLow)} → Climate suitability
            </span>
          </div>
        </div>
      )}

      {performanceResults.rutting.length > 0 ? (
        <>
          <div style={S.grid2}>
            <div style={S.card}>
              <h3 style={S.cardTitle}>
                📉 Rutting{" "}
                {useBiobinderInAnalysis && (
                  <span
                    style={S.badge(
                      performanceResults.rutting[
                        performanceResults.rutting.length - 1
                      ]?.binderEffect > 1
                        ? "orange"
                        : "green"
                    )}
                  >
                    {performanceResults.rutting[
                      performanceResults.rutting.length - 1
                    ]?.binderEffect > 1
                      ? "⚠️ Higher"
                      : "✓ OK"}
                  </span>
                )}
              </h3>
              {useBiobinderInAnalysis && (
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    marginBottom: "0.5rem",
                  }}
                >
                  Binder effect factor:{" "}
                  {performanceResults.rutting[
                    performanceResults.rutting.length - 1
                  ]?.binderEffect?.toFixed(3) || 1.0}
                  x
                  {performanceResults.rutting[
                    performanceResults.rutting.length - 1
                  ]?.binderEffect > 1 &&
                    " (Lower G*/sin(δ) increases rutting susceptibility)"}
                </div>
              )}
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={performanceResults.rutting}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip
                    formatter={(v, name) => [
                      typeof v === "number" ? v.toFixed(2) : v,
                      name,
                    ]}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="rutting"
                    stroke="#dc2626"
                    name="Rutting (mm)"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="limit"
                    stroke="#d97706"
                    name="Limit"
                    strokeDasharray="5 5"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={S.card}>
              <h3 style={S.cardTitle}>
                🔀 Cracking{" "}
                {useBiobinderInAnalysis && (
                  <span
                    style={S.badge(
                      performanceResults.cracking[
                        performanceResults.cracking.length - 1
                      ]?.binderEffect > 1
                        ? "orange"
                        : "green"
                    )}
                  >
                    {performanceResults.cracking[
                      performanceResults.cracking.length - 1
                    ]?.binderEffect > 1
                      ? "⚠️ Higher"
                      : "✓ OK"}
                  </span>
                )}
              </h3>
              {useBiobinderInAnalysis && (
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    marginBottom: "0.5rem",
                  }}
                >
                  Binder effect factor:{" "}
                  {performanceResults.cracking[
                    performanceResults.cracking.length - 1
                  ]?.binderEffect?.toFixed(3) || 1.0}
                  x
                  {performanceResults.cracking[
                    performanceResults.cracking.length - 1
                  ]?.binderEffect > 1 &&
                    " (Lower m-value or higher stiffness increases cracking)"}
                </div>
              )}
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={performanceResults.cracking}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip
                    formatter={(v, name) => [
                      typeof v === "number" ? v.toFixed(2) : v,
                      name,
                    ]}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="cracking"
                    stroke="#7c3aed"
                    name="Cracking (%)"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="limit"
                    stroke="#d97706"
                    name="Limit"
                    strokeDasharray="5 5"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={{ ...S.card, marginTop: "1.5rem" }}>
            <h3 style={S.cardTitle}>📊 End of Design Life Summary</h3>
            <div style={S.grid4}>
              <MetricCard
                title="Final Rutting"
                value={`${performanceResults.rutting[
                  performanceResults.rutting.length - 1
                ]?.rutting.toFixed(1)} mm`}
                color={
                  performanceResults.rutting[
                    performanceResults.rutting.length - 1
                  ]?.rutting >
                  performanceResults.rutting[
                    performanceResults.rutting.length - 1
                  ]?.limit
                    ? "red"
                    : "green"
                }
              />
              <MetricCard
                title="Final Cracking"
                value={`${performanceResults.cracking[
                  performanceResults.cracking.length - 1
                ]?.cracking.toFixed(1)}%`}
                color={
                  performanceResults.cracking[
                    performanceResults.cracking.length - 1
                  ]?.cracking >
                  performanceResults.cracking[
                    performanceResults.cracking.length - 1
                  ]?.limit
                    ? "red"
                    : "green"
                }
              />
              <MetricCard
                title="Final IRI"
                value={performanceResults.roughness[
                  performanceResults.roughness.length - 1
                ]?.iri.toFixed(2)}
                color="green"
              />
              <MetricCard
                title="NAASRA"
                value={performanceResults.roughness[
                  performanceResults.roughness.length - 1
                ]?.naasra.toFixed(0)}
                color="blue"
              />
            </div>

            {/* Biobinder Performance Summary */}
            {useBiobinderInAnalysis && (
              <div
                style={{
                  marginTop: "1.5rem",
                  padding: "1rem",
                  background: "#f0fdf4",
                  borderRadius: "0.75rem",
                }}
              >
                <h4
                  style={{
                    fontWeight: "bold",
                    color: "#065f46",
                    marginBottom: "0.5rem",
                  }}
                >
                  🧪 Biobinder Performance Impact
                </h4>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                    gap: "1rem",
                    fontSize: "0.875rem",
                  }}
                >
                  <div>
                    <strong>Rutting:</strong>{" "}
                    {performanceResults.rutting[
                      performanceResults.rutting.length - 1
                    ]?.binderEffect > 1.05
                      ? "⚠️ Slightly increased"
                      : performanceResults.rutting[
                          performanceResults.rutting.length - 1
                        ]?.binderEffect < 0.95
                      ? "✅ Improved"
                      : "➡️ Similar"}{" "}
                    due to G*/sin(δ) ={" "}
                    {effectiveBinderProps.gStar_sinDelta.toFixed(2)} kPa
                  </div>
                  <div>
                    <strong>Cracking:</strong>{" "}
                    {performanceResults.cracking[
                      performanceResults.cracking.length - 1
                    ]?.binderEffect > 1.05
                      ? "⚠️ Slightly increased"
                      : performanceResults.cracking[
                          performanceResults.cracking.length - 1
                        ]?.binderEffect < 0.95
                      ? "✅ Improved"
                      : "➡️ Similar"}{" "}
                    due to m-value = {effectiveBinderProps.mValue.toFixed(2)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div style={S.emptyState}>
          <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>📊</div>
          <h3>Run Analysis</h3>
          <p style={{ color: "#6b7280" }}>
            Click "Run Analysis" to generate predictions
            {useBiobinderInAnalysis && " with biobinder rheology effects"}
          </p>
        </div>
      )}
    </div>
  );

  const CarbonTab = () => (
    <div>
      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <h2 style={S.sectionTitle}>🌱 Carbon & ETS</h2>
        <p style={S.sectionSub}>
          Carbon footprint with NZ ETS -{" "}
          {useBiobinderInAnalysis ? "Biobinder Enabled" : "Standard Bitumen"}
        </p>
      </div>

      {/* Biobinder Status Banner */}
      {useBiobinderInAnalysis ? (
        <div
          style={{
            ...S.infoBox("success"),
            marginBottom: "1.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <strong>🌿 Biobinder Enabled:</strong> {effectiveBinderProps.name} (
            {biobinderBlendRatio}% replacement)
            <div style={{ fontSize: "0.8rem", marginTop: "0.25rem" }}>
              Carbon intensity reduced from {DEFAULT_BITUMEN.carbonIntensity} to{" "}
              {Math.round(effectiveBinderProps.carbonIntensity)} kgCO₂e/t
            </div>
          </div>
          <span
            style={{
              ...S.badge("green"),
              fontSize: "1rem",
              padding: "0.5rem 1rem",
            }}
          >
            -
            {Math.round(
              ((DEFAULT_BITUMEN.carbonIntensity -
                effectiveBinderProps.carbonIntensity) /
                DEFAULT_BITUMEN.carbonIntensity) *
                100
            )}
            % Binder Carbon
          </span>
        </div>
      ) : (
        <div style={{ ...S.infoBox("warning"), marginBottom: "1.5rem" }}>
          <strong>⚠️ Using Standard Bitumen:</strong> Enable biobinder in the
          Biobinder Rheology tab to see carbon savings.
        </div>
      )}

      <div style={S.grid3}>
        <MetricCard
          title="Asphalt Carbon"
          value={`${carbonAnalysis.asphaltCarbon}`}
          unit="kgCO₂e/m"
          color="red"
          icon="🏗️"
        />
        <MetricCard
          title="Base Carbon"
          value={`${carbonAnalysis.basecourseCarbon}`}
          unit="kgCO₂e/m"
          color="orange"
          icon="🪨"
        />
        <MetricCard
          title="Total"
          value={`${carbonAnalysis.totalCarbon}`}
          unit="kgCO₂e/m"
          color="purple"
          icon="📊"
        />
      </div>

      {/* Binder Carbon Breakdown */}
      <div style={{ ...S.card, marginTop: "1.5rem" }}>
        <h3 style={S.cardTitle}>🔬 Binder Carbon Analysis</h3>
        <div style={S.grid4}>
          <MetricCard
            title="Binder Carbon"
            value={carbonAnalysis.binderCarbon}
            unit="kgCO₂e/m"
            color={useBiobinderInAnalysis ? "green" : "orange"}
            icon="🛢️"
          />
          <MetricCard
            title="Binder Type"
            value={useBiobinderInAnalysis ? "Bio" : "Std"}
            unit={carbonAnalysis.binderName}
            color={useBiobinderInAnalysis ? "green" : "blue"}
            icon={useBiobinderInAnalysis ? "🌿" : "🛢️"}
          />
          {useBiobinderInAnalysis && (
            <>
              <MetricCard
                title="Binder Savings"
                value={carbonAnalysis.binderCarbonSavings}
                unit="kgCO₂e/m saved"
                color="green"
                icon="📉"
              />
              <MetricCard
                title="Savings %"
                value={`${carbonAnalysis.binderCarbonSavingsPercent}%`}
                unit="vs standard bitumen"
                color="green"
                icon="✅"
              />
            </>
          )}
        </div>
      </div>

      <div style={{ ...S.grid2, marginTop: "1.5rem" }}>
        <MetricCard
          title="ETS Cost (2025)"
          value={`$${carbonAnalysis.carbonCostCurrent}`}
          unit="per meter"
          color="blue"
          icon="💸"
        />
        <MetricCard
          title="ETS Cost (2030)"
          value={`$${carbonAnalysis.carbonCost2030}`}
          unit="per meter"
          color="orange"
          icon="📈"
        />
      </div>

      {/* Comparison: Standard vs Biobinder */}
      {useBiobinderInAnalysis && (
        <div
          style={{
            ...S.card,
            marginTop: "1.5rem",
            background: "linear-gradient(135deg, #f0fdf4, #ecfdf5)",
          }}
        >
          <h3 style={S.cardTitle}>
            📊 Standard Bitumen vs Biobinder Comparison
          </h3>
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Component</th>
                  <th style={{ ...S.th, background: "#fef3c7" }}>
                    Standard Bitumen
                  </th>
                  <th style={{ ...S.th, background: "#dcfce7" }}>
                    {effectiveBinderProps.name}
                  </th>
                  <th style={S.th}>Savings</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={S.td}>Binder Carbon Intensity</td>
                  <td style={{ ...S.td, background: "#fffbeb" }}>
                    {DEFAULT_BITUMEN.carbonIntensity} kgCO₂e/t
                  </td>
                  <td style={{ ...S.td, background: "#ecfdf5" }}>
                    {Math.round(effectiveBinderProps.carbonIntensity)} kgCO₂e/t
                  </td>
                  <td style={{ ...S.td, color: "#059669", fontWeight: "bold" }}>
                    -
                    {Math.round(
                      ((DEFAULT_BITUMEN.carbonIntensity -
                        effectiveBinderProps.carbonIntensity) /
                        DEFAULT_BITUMEN.carbonIntensity) *
                        100
                    )}
                    %
                  </td>
                </tr>
                <tr>
                  <td style={S.td}>Binder Carbon per meter</td>
                  <td style={{ ...S.td, background: "#fffbeb" }}>
                    {(
                      parseFloat(carbonAnalysis.binderCarbon) +
                      parseFloat(carbonAnalysis.binderCarbonSavings)
                    ).toFixed(2)}{" "}
                    kgCO₂e
                  </td>
                  <td style={{ ...S.td, background: "#ecfdf5" }}>
                    {carbonAnalysis.binderCarbon} kgCO₂e
                  </td>
                  <td style={{ ...S.td, color: "#059669", fontWeight: "bold" }}>
                    -{carbonAnalysis.binderCarbonSavings} kgCO₂e
                  </td>
                </tr>
                <tr>
                  <td style={S.td}>Bio Content</td>
                  <td style={{ ...S.td, background: "#fffbeb" }}>0%</td>
                  <td style={{ ...S.td, background: "#ecfdf5" }}>
                    {effectiveBinderProps.bioContent?.toFixed(1) || 0}%
                  </td>
                  <td style={{ ...S.td, color: "#059669", fontWeight: "bold" }}>
                    +{effectiveBinderProps.bioContent?.toFixed(1) || 0}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const LCATab = () => {
    const moduleEntries = (mod) =>
      allLcaEntries.filter((e) => e.module === mod);
    const moduleTotal = (mod) =>
      moduleEntries(mod).reduce((s, e) => s + e.total, 0);
    const moduleAutoCount = (mod) =>
      autoLcaEntries.filter((e) => e.module === mod).length;
    const moduleManualCount = (mod) =>
      lcaEntries.filter((e) => e.module === mod).length;

    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "2rem",
          }}
        >
          <div>
            <h2 style={S.sectionTitle}>♻️ Life Cycle Assessment</h2>
            <p style={S.sectionSub}>
              EN15804 compliant analysis - Auto-populated from Materials +
              Manual entries
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".csv"
              style={{ display: "none" }}
            />
            <button
              style={S.btnSecondary}
              onClick={() => fileInputRef.current?.click()}
            >
              📤 Upload EPD
            </button>
          </div>
        </div>

        {/* Auto-population Info Banner */}
        <div
          style={{
            ...S.infoBox("info"),
            marginBottom: "1.5rem",
            display: "flex",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <span style={{ fontSize: "2rem" }}>🔗</span>
          <div>
            <strong>Auto-Populated from Carbon Tab:</strong> Material entries
            (A1, A3) are automatically calculated from your selections in the
            Materials tab.
            <div style={{ fontSize: "0.8rem", marginTop: "0.25rem" }}>
              Asphalt: {materialProps.asphaltThickness}mm {asphaltType} •
              Basecourse: {materialProps.baseThickness}mm {basecourseType}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div style={S.grid4}>
          <MetricCard
            title="Total Emissions"
            value={`${Math.round(lcaTotalEmissions).toLocaleString()}`}
            unit="kgCO₂e"
            color="red"
            icon="🌍"
          />
          <MetricCard
            title="Auto (Materials)"
            value={`${Math.round(lcaAutoTotal).toLocaleString()}`}
            unit="kgCO₂e"
            color="blue"
            icon="🔗"
          />
          <MetricCard
            title="Manual Entries"
            value={`${Math.round(lcaManualTotal).toLocaleString()}`}
            unit="kgCO₂e"
            color="purple"
            icon="✏️"
          />
          <MetricCard
            title="Total Entries"
            value={`${allLcaEntries.length}`}
            unit={`${autoLcaEntries.length} auto + ${lcaEntries.length} manual`}
            color="green"
            icon="📋"
          />
        </div>

        {/* Scope Breakdown */}
        <div style={{ ...S.grid3, marginTop: "1rem" }}>
          <MetricCard
            title="Scope 1 (Direct)"
            value={`${Math.round(lcaBreakdown.scope1).toLocaleString()}`}
            unit="kgCO₂e"
            color="red"
            icon="🏭"
          />
          <MetricCard
            title="Scope 2 (Energy)"
            value={`${Math.round(lcaBreakdown.scope2).toLocaleString()}`}
            unit="kgCO₂e"
            color="orange"
            icon="⚡"
          />
          <MetricCard
            title="Scope 3 (Indirect)"
            value={`${Math.round(lcaBreakdown.scope3).toLocaleString()}`}
            unit="kgCO₂e"
            color="purple"
            icon="🔗"
          />
        </div>

        {/* EN15804 Module Grid */}
        <div style={{ ...S.card, marginTop: "1.5rem" }}>
          <h3 style={S.cardTitle}>
            📊 EN15804 Lifecycle Modules - Click to Add Manual Entries
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
              gap: "0.75rem",
            }}
          >
            {Object.entries(EN15804).map(([key, name]) => {
              const tot = moduleTotal(key);
              const autoCount = moduleAutoCount(key);
              const manualCount = moduleManualCount(key);
              const hasAuto = autoCount > 0;
              const hasManual = manualCount > 0;

              return (
                <button
                  key={key}
                  onClick={() => onModuleClick(key)}
                  style={{
                    padding: "1rem 0.5rem",
                    borderRadius: "0.75rem",
                    border: hasAuto
                      ? "2px solid #3b82f6"
                      : hasManual
                      ? "2px solid #8b5cf6"
                      : "2px dashed #d1d5db",
                    background:
                      hasAuto && hasManual
                        ? "linear-gradient(135deg, #dbeafe, #ede9fe)"
                        : hasAuto
                        ? "#dbeafe"
                        : hasManual
                        ? "#ede9fe"
                        : "#f9fafb",
                    cursor: "pointer",
                    textAlign: "center",
                    transition: "all 0.2s",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: "bold",
                      color: "#374151",
                    }}
                  >
                    {key}
                  </div>
                  <div
                    style={{
                      fontSize: "0.65rem",
                      color: "#6b7280",
                      marginBottom: "0.25rem",
                    }}
                  >
                    {name}
                  </div>
                  {tot > 0 ? (
                    <>
                      <div
                        style={{
                          fontSize: "1rem",
                          fontWeight: "bold",
                          color: "#059669",
                        }}
                      >
                        {Math.round(tot).toLocaleString()}
                      </div>
                      <div style={{ fontSize: "0.6rem", color: "#6b7280" }}>
                        kgCO₂e
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "0.25rem",
                          marginTop: "0.25rem",
                        }}
                      >
                        {hasAuto && (
                          <span
                            style={{
                              ...S.badge("blue"),
                              fontSize: "0.55rem",
                              padding: "0.1rem 0.4rem",
                            }}
                          >
                            {autoCount} auto
                          </span>
                        )}
                        {hasManual && (
                          <span
                            style={{
                              ...S.badge("purple"),
                              fontSize: "0.55rem",
                              padding: "0.1rem 0.4rem",
                            }}
                          >
                            {manualCount} manual
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#9ca3af",
                        marginTop: "0.5rem",
                      }}
                    >
                      + Add
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div
            style={{
              display: "flex",
              gap: "1.5rem",
              marginTop: "1rem",
              fontSize: "0.75rem",
              color: "#6b7280",
            }}
          >
            <span>
              <span
                style={{
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  background: "#dbeafe",
                  border: "2px solid #3b82f6",
                  borderRadius: "3px",
                  marginRight: "0.25rem",
                }}
              ></span>{" "}
              Auto-populated
            </span>
            <span>
              <span
                style={{
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  background: "#ede9fe",
                  border: "2px solid #8b5cf6",
                  borderRadius: "3px",
                  marginRight: "0.25rem",
                }}
              ></span>{" "}
              Manual entry
            </span>
            <span>
              <span
                style={{
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  background: "linear-gradient(135deg, #dbeafe, #ede9fe)",
                  border: "2px solid #6366f1",
                  borderRadius: "3px",
                  marginRight: "0.25rem",
                }}
              ></span>{" "}
              Both
            </span>
          </div>
        </div>

        {/* Entries Table */}
        <div style={{ ...S.card, marginTop: "1.5rem" }}>
          <h3 style={S.cardTitle}>
            📋 All LCA Entries ({allLcaEntries.length} total)
          </h3>

          {/* Auto-populated entries section */}
          {autoLcaEntries.length > 0 && (
            <>
              <div
                style={{
                  background: "#eff6ff",
                  padding: "0.75rem 1rem",
                  borderRadius: "0.5rem",
                  marginBottom: "1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <span style={{ fontSize: "1.25rem" }}>🔗</span>
                <span style={{ fontWeight: "600", color: "#1e40af" }}>
                  Auto-Populated from Materials Tab ({autoLcaEntries.length}{" "}
                  entries, {Math.round(lcaAutoTotal).toLocaleString()} kgCO₂e)
                </span>
              </div>
              <div style={{ overflowX: "auto", marginBottom: "1.5rem" }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Module</th>
                      <th style={S.th}>Process</th>
                      <th style={S.th}>Mass (kg)</th>
                      <th style={S.th}>EF (kgCO₂e/t)</th>
                      <th style={S.th}>Total (kgCO₂e)</th>
                      <th style={S.th}>Scope</th>
                      <th style={S.th}>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoLcaEntries.map((e, i) => (
                      <tr key={`auto-${i}`} style={{ background: "#f0f9ff" }}>
                        <td style={S.td}>
                          <span style={S.badge("blue")}>{e.module}</span>
                        </td>
                        <td style={{ ...S.td, fontWeight: "500" }}>
                          {e.title}
                        </td>
                        <td style={S.td}>
                          {Math.round(e.mass).toLocaleString()}
                        </td>
                        <td style={S.td}>{e.ef.toFixed(1)}</td>
                        <td
                          style={{
                            ...S.td,
                            fontWeight: "bold",
                            color: "#059669",
                          }}
                        >
                          {Math.round(e.total).toLocaleString()}
                        </td>
                        <td style={S.td}>
                          <span
                            style={S.badge(
                              e.scope === "1"
                                ? "red"
                                : e.scope === "2"
                                ? "orange"
                                : "purple"
                            )}
                          >
                            S{e.scope}
                          </span>
                        </td>
                        <td style={S.td}>
                          <span
                            style={{ fontSize: "0.7rem", color: "#3b82f6" }}
                          >
                            🔗 {e.source}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Manual entries section */}
          {lcaEntries.length > 0 ? (
            <>
              <div
                style={{
                  background: "#f5f3ff",
                  padding: "0.75rem 1rem",
                  borderRadius: "0.5rem",
                  marginBottom: "1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <span style={{ fontSize: "1.25rem" }}>✏️</span>
                <span style={{ fontWeight: "600", color: "#6b21a8" }}>
                  Manual Entries ({lcaEntries.length} entries,{" "}
                  {Math.round(lcaManualTotal).toLocaleString()} kgCO₂e)
                </span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Module</th>
                      <th style={S.th}>Process</th>
                      <th style={S.th}>Mass (kg)</th>
                      <th style={S.th}>EF</th>
                      <th style={S.th}>Total (kgCO₂e)</th>
                      <th style={S.th}>Scope</th>
                      <th style={S.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lcaEntries.map((e, i) => (
                      <tr key={`manual-${i}`} style={{ background: "#faf5ff" }}>
                        <td style={S.td}>
                          <span style={S.badge("purple")}>{e.module}</span>
                        </td>
                        <td style={{ ...S.td, fontWeight: "500" }}>
                          {e.title || "—"}
                        </td>
                        <td style={S.td}>{e.mass.toLocaleString()}</td>
                        <td style={S.td}>{e.ef}</td>
                        <td
                          style={{
                            ...S.td,
                            fontWeight: "bold",
                            color: "#059669",
                          }}
                        >
                          {e.total.toFixed(1)}
                        </td>
                        <td style={S.td}>
                          <span
                            style={S.badge(
                              e.scope === "1"
                                ? "red"
                                : e.scope === "2"
                                ? "orange"
                                : "purple"
                            )}
                          >
                            S{e.scope}
                          </span>
                        </td>
                        <td style={S.td}>
                          <button
                            onClick={() =>
                              setLcaEntries((p) =>
                                p.filter((_, idx) => idx !== i)
                              )
                            }
                            style={{
                              background: "#fee2e2",
                              border: "none",
                              borderRadius: "0.25rem",
                              padding: "0.25rem 0.5rem",
                              cursor: "pointer",
                              fontSize: "0.75rem",
                              color: "#dc2626",
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div
              style={{
                background: "#f5f3ff",
                padding: "1.5rem",
                borderRadius: "0.75rem",
                textAlign: "center",
                marginTop: "1rem",
              }}
            >
              <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>✏️</div>
              <div style={{ fontWeight: "600", color: "#6b21a8" }}>
                No Manual Entries Yet
              </div>
              <div style={{ fontSize: "0.875rem", color: "#7c3aed" }}>
                Click on any module above to add transport, construction,
                maintenance, or other processes
              </div>
            </div>
          )}
        </div>

        {/* Suggested Additional Entries */}
        <div
          style={{
            ...S.card,
            marginTop: "1.5rem",
            background: "#fffbeb",
            border: "1px solid #fcd34d",
          }}
        >
          <h3 style={{ ...S.cardTitle, color: "#92400e" }}>
            💡 Suggested Additional Entries
          </h3>
          <p
            style={{
              fontSize: "0.875rem",
              color: "#a16207",
              marginBottom: "1rem",
            }}
          >
            The auto-populated entries cover raw materials (A1) and
            manufacturing (A3). Consider adding these processes for a complete
            LCA:
          </p>
          <div style={S.grid2}>
            <div>
              <div
                style={{
                  fontWeight: "600",
                  color: "#92400e",
                  marginBottom: "0.5rem",
                }}
              >
                🚛 Transport (A2, A4)
              </div>
              <ul
                style={{
                  fontSize: "0.8rem",
                  color: "#a16207",
                  margin: 0,
                  paddingLeft: "1.25rem",
                }}
              >
                <li>A2: Aggregate transport to asphalt plant</li>
                <li>A2: Bitumen delivery to plant</li>
                <li>A4: Asphalt mix delivery to site</li>
                <li>A4: Basecourse delivery to site</li>
              </ul>
            </div>
            <div>
              <div
                style={{
                  fontWeight: "600",
                  color: "#92400e",
                  marginBottom: "0.5rem",
                }}
              >
                🏗️ Construction (A5)
              </div>
              <ul
                style={{
                  fontSize: "0.8rem",
                  color: "#a16207",
                  margin: 0,
                  paddingLeft: "1.25rem",
                }}
              >
                <li>Paver fuel consumption</li>
                <li>Roller compaction energy</li>
                <li>Site preparation equipment</li>
              </ul>
            </div>
            <div>
              <div
                style={{
                  fontWeight: "600",
                  color: "#92400e",
                  marginBottom: "0.5rem",
                }}
              >
                🔧 Use & Maintenance (B1, B2)
              </div>
              <ul
                style={{
                  fontSize: "0.8rem",
                  color: "#a16207",
                  margin: 0,
                  paddingLeft: "1.25rem",
                }}
              >
                <li>B1: Vehicle fuel efficiency impact</li>
                <li>B2: Resealing operations</li>
                <li>B2: Pothole repairs</li>
              </ul>
            </div>
            <div>
              <div
                style={{
                  fontWeight: "600",
                  color: "#92400e",
                  marginBottom: "0.5rem",
                }}
              >
                ♻️ End of Life (C4, D)
              </div>
              <ul
                style={{
                  fontSize: "0.8rem",
                  color: "#a16207",
                  margin: 0,
                  paddingLeft: "1.25rem",
                }}
              >
                <li>C4: Demolition & disposal</li>
                <li>D: RAP recycling credits (negative)</li>
                <li>D: Aggregate recovery benefits</li>
              </ul>
            </div>
          </div>
        </div>

        {/* LCA Dialog */}
        {showLcaDialog && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "1rem",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "1rem",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
                width: "100%",
                maxWidth: "500px",
                maxHeight: "90vh",
                overflow: "auto",
              }}
            >
              <div
                style={{
                  padding: "1.5rem",
                  borderBottom: "1px solid #e5e7eb",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "#f5f3ff",
                }}
              >
                <div>
                  <h3
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: "bold",
                      color: "#6b21a8",
                    }}
                  >
                    ✏️ Add Manual Entry to {activeLcaModule}
                  </h3>
                  <p
                    style={{ fontSize: "0.8rem", color: "#7c3aed", margin: 0 }}
                  >
                    {EN15804[activeLcaModule]}
                  </p>
                </div>
                <button
                  onClick={() => setShowLcaDialog(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "1.5rem",
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>
              <div style={{ padding: "1.5rem" }}>
                {/* Show existing entries for this module */}
                {moduleEntries(activeLcaModule).length > 0 && (
                  <div
                    style={{
                      marginBottom: "1.5rem",
                      padding: "1rem",
                      background: "#f9fafb",
                      borderRadius: "0.5rem",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: "600",
                        color: "#6b7280",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Existing entries in {activeLcaModule}:
                    </div>
                    {moduleEntries(activeLcaModule).map((e, i) => (
                      <div
                        key={i}
                        style={{
                          fontSize: "0.8rem",
                          color: "#374151",
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "0.25rem 0",
                        }}
                      >
                        <span>
                          {e.isAuto ? "🔗" : "✏️"} {e.title}
                        </span>
                        <span style={{ fontWeight: "600" }}>
                          {Math.round(e.total).toLocaleString()} kgCO₂e
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <InputField
                  label="Process Title"
                  value={lcaForm.title}
                  onChange={(v) => setLcaForm((f) => ({ ...f, title: v }))}
                  type="text"
                  helpText="e.g., Truck transport 50km, Paver operation"
                />
                <SelectField
                  label="GHG Scope"
                  value={lcaForm.scope}
                  onChange={(v) => setLcaForm((f) => ({ ...f, scope: v }))}
                  options={[
                    { value: "", label: "Select Scope..." },
                    {
                      value: "1",
                      label: "Scope 1 - Direct emissions (fuel combustion)",
                    },
                    {
                      value: "2",
                      label: "Scope 2 - Indirect (purchased electricity)",
                    },
                    {
                      value: "3",
                      label: "Scope 3 - Value chain (materials, transport)",
                    },
                  ]}
                />
                <InputField
                  label="Mass / Quantity"
                  value={lcaForm.mass}
                  onChange={(v) => setLcaForm((f) => ({ ...f, mass: v }))}
                  unit="kg or units"
                  helpText="e.g., fuel kg, material tonnes, equipment hours"
                />

                {Object.keys(lcaFactors).length > 0 ? (
                  <SelectField
                    label="Emission Factor (from EPD)"
                    value={lcaForm.label}
                    onChange={(v) => setLcaForm((f) => ({ ...f, label: v }))}
                    options={[
                      { value: "", label: "Select from database..." },
                      ...Object.keys(lcaFactors).map((k) => ({
                        value: k,
                        label: `${k} (${lcaFactors[k]} kgCO₂e)`,
                      })),
                    ]}
                  />
                ) : (
                  <div
                    style={{ ...S.infoBox("warning"), marginBottom: "1rem" }}
                  >
                    <strong>No EPD database loaded.</strong> Use custom emission
                    factor below, or upload a CSV with emission factors.
                  </div>
                )}

                <InputField
                  label="Custom Emission Factor"
                  value={lcaForm.customEpd}
                  onChange={(v) => setLcaForm((f) => ({ ...f, customEpd: v }))}
                  unit="kgCO₂e per unit"
                  helpText="Override EPD selection or enter custom value"
                />

                {/* Common emission factors reference */}
                <div
                  style={{
                    marginTop: "1rem",
                    padding: "0.75rem",
                    background: "#f0fdf4",
                    borderRadius: "0.5rem",
                    fontSize: "0.75rem",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "600",
                      color: "#065f46",
                      marginBottom: "0.25rem",
                    }}
                  >
                    📚 Common Emission Factors (NZ):
                  </div>
                  <div style={{ color: "#047857" }}>
                    Diesel: 2.68 kgCO₂e/L • Petrol: 2.31 kgCO₂e/L • Electricity:
                    0.08 kgCO₂e/kWh • Trucking: 0.1 kgCO₂e/t-km
                  </div>
                </div>
              </div>
              <div
                style={{
                  padding: "1.5rem",
                  borderTop: "1px solid #e5e7eb",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "1rem",
                  background: "#f9fafb",
                }}
              >
                <button
                  style={S.btnSecondary}
                  onClick={() => setShowLcaDialog(false)}
                >
                  Cancel
                </button>
                <button style={S.btnPrimary} onClick={addLcaEntry}>
                  ✅ Add Entry
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const EconomicTab = () => {
    const mechanism = carbonMechanisms[carbonMechanism];

    const updateMechanismPrice = (mechKey, year, value) => {
      setCarbonMechanisms((prev) => ({
        ...prev,
        [mechKey]: {
          ...prev[mechKey],
          forecast: {
            ...prev[mechKey].forecast,
            [year]: Number(value),
          },
          currentPrice:
            year === 2025 ? Number(value) : prev[mechKey].currentPrice,
        },
      }));
    };

    const updateComplianceCost = (mechKey, value) => {
      setCarbonMechanisms((prev) => ({
        ...prev,
        [mechKey]: {
          ...prev[mechKey],
          complianceCost: Number(value),
        },
      }));
    };

    const resetToDefaults = () => {
      setCarbonMechanisms(
        JSON.parse(JSON.stringify(DEFAULT_CARBON_MECHANISMS))
      );
    };

    return (
      <div>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <h2 style={S.sectionTitle}>💰 Economic Modeling with Carbon Costs</h2>
          <p style={S.sectionSub}>
            NZ ETS, Carbon Tax & Hybrid Approach - Editable Parameters
          </p>
        </div>

        {/* Carbon Cost Mechanism Selection */}
        <div
          style={{
            ...S.card,
            marginBottom: "1.5rem",
            background: "linear-gradient(135deg, #f0fdf4, #ecfdf5)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h3 style={S.cardTitle}>🌱 Carbon Cost Mechanism Selection</h3>
            <button
              style={{
                ...S.btnSecondary,
                fontSize: "0.75rem",
                padding: "0.5rem 1rem",
              }}
              onClick={resetToDefaults}
            >
              🔄 Reset to Defaults
            </button>
          </div>
          <div style={S.grid3}>
            {Object.entries(carbonMechanisms).map(([key, mech]) => (
              <div
                key={key}
                onClick={() => setCarbonMechanism(key)}
                style={{
                  padding: "1.25rem",
                  borderRadius: "1rem",
                  border:
                    carbonMechanism === key
                      ? "3px solid #059669"
                      : "2px solid #d1d5db",
                  background: carbonMechanism === key ? "white" : "#f9fafb",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "0.5rem",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "bold",
                      color: carbonMechanism === key ? "#059669" : "#374151",
                    }}
                  >
                    {key === "ets" ? "📊" : key === "carbonTax" ? "🏛️" : "⚖️"}{" "}
                    {key === "ets"
                      ? "ETS"
                      : key === "carbonTax"
                      ? "Carbon Tax"
                      : "Hybrid"}
                  </span>
                  {carbonMechanism === key && (
                    <span style={S.badge("green")}>Selected</span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    color: "#059669",
                  }}
                >
                  ${mech.currentPrice}/t
                </div>
                <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                  Volatility: {mech.priceVolatility}
                </div>
              </div>
            ))}
          </div>

          {/* Mechanism Details */}
          <div
            style={{
              marginTop: "1.5rem",
              padding: "1rem",
              background: "#f0fdf4",
              borderRadius: "0.75rem",
              border: "1px solid #86efac",
            }}
          >
            <div
              style={{
                fontWeight: "bold",
                marginBottom: "0.5rem",
                color: "#065f46",
              }}
            >
              {mechanism.name}
            </div>
            <div
              style={{
                fontSize: "0.875rem",
                color: "#047857",
                marginBottom: "0.75rem",
              }}
            >
              {mechanism.description}
            </div>
            <div style={S.grid2}>
              <div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: "600",
                    color: "#065f46",
                    marginBottom: "0.25rem",
                  }}
                >
                  ✅ Advantages
                </div>
                <ul
                  style={{
                    fontSize: "0.75rem",
                    color: "#047857",
                    margin: 0,
                    paddingLeft: "1rem",
                  }}
                >
                  {mechanism.advantages.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: "600",
                    color: "#991b1b",
                    marginBottom: "0.25rem",
                  }}
                >
                  ⚠️ Disadvantages
                </div>
                <ul
                  style={{
                    fontSize: "0.75rem",
                    color: "#dc2626",
                    margin: 0,
                    paddingLeft: "1rem",
                  }}
                >
                  {mechanism.disadvantages.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Editable Price Forecast Table */}
        <div style={{ ...S.card, marginBottom: "1.5rem" }}>
          <h3 style={S.cardTitle}>
            📈 Carbon Price Forecast - Click to Edit (NZD/tonne CO₂e)
          </h3>
          <div
            style={{
              ...S.infoBox("info"),
              marginBottom: "1rem",
              fontSize: "0.8rem",
            }}
          >
            💡 <strong>Tip:</strong> Click on any price cell to edit. Changes
            will automatically update all economic calculations.
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Mechanism</th>
                  <th style={S.th}>2025</th>
                  <th style={S.th}>2030</th>
                  <th style={S.th}>2035</th>
                  <th style={S.th}>2040</th>
                  <th style={S.th}>2050</th>
                  <th style={S.th}>Compliance %</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(carbonMechanisms).map(([key, mech]) => (
                  <tr
                    key={key}
                    style={{
                      background: carbonMechanism === key ? "#ecfdf5" : "white",
                    }}
                  >
                    <td style={{ ...S.td, fontWeight: "600" }}>
                      {key === "ets"
                        ? "📊 ETS"
                        : key === "carbonTax"
                        ? "🏛️ Carbon Tax"
                        : "⚖️ Hybrid"}
                    </td>
                    {[2025, 2030, 2035, 2040, 2050].map((year) => (
                      <td key={year} style={S.td}>
                        <input
                          type="number"
                          value={mech.forecast[year]}
                          onChange={(e) =>
                            updateMechanismPrice(key, year, e.target.value)
                          }
                          style={{
                            width: "70px",
                            padding: "0.25rem 0.5rem",
                            border: "1px solid #d1d5db",
                            borderRadius: "0.25rem",
                            fontSize: "0.875rem",
                            textAlign: "center",
                          }}
                        />
                      </td>
                    ))}
                    <td style={S.td}>
                      <input
                        type="number"
                        value={mech.complianceCost}
                        onChange={(e) =>
                          updateComplianceCost(key, e.target.value)
                        }
                        step="0.1"
                        style={{
                          width: "60px",
                          padding: "0.25rem 0.5rem",
                          border: "1px solid #d1d5db",
                          borderRadius: "0.25rem",
                          fontSize: "0.875rem",
                          textAlign: "center",
                        }}
                      />
                    </td>
                  </tr>
                ))}
                {carbonMechanism === "hybrid" && (
                  <>
                    <tr style={{ background: "#fef3c7" }}>
                      <td style={{ ...S.td, fontSize: "0.75rem" }}>
                        ↳ Price Floor
                      </td>
                      {[2025, 2030, 2035, 2040, 2050].map((year) => (
                        <td key={year} style={{ ...S.td, fontSize: "0.75rem" }}>
                          ${carbonMechanisms.hybrid.priceFloor?.[year] || "-"}
                        </td>
                      ))}
                      <td style={S.td}>-</td>
                    </tr>
                    <tr style={{ background: "#fee2e2" }}>
                      <td style={{ ...S.td, fontSize: "0.75rem" }}>
                        ↳ Price Ceiling
                      </td>
                      {[2025, 2030, 2035, 2040, 2050].map((year) => (
                        <td key={year} style={{ ...S.td, fontSize: "0.75rem" }}>
                          ${carbonMechanisms.hybrid.priceCeiling?.[year] || "-"}
                        </td>
                      ))}
                      <td style={S.td}>-</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mechanism Comparison */}
        <div style={{ ...S.card, marginBottom: "1.5rem" }}>
          <h3 style={S.cardTitle}>
            ⚖️ Total Carbon Cost Comparison (Project Lifetime)
          </h3>
          <div style={S.grid3}>
            {economicAnalysis.mechanismComparison.map((comp) => (
              <div
                key={comp.key}
                style={{
                  padding: "1.25rem",
                  borderRadius: "1rem",
                  background:
                    carbonMechanism === comp.key
                      ? "linear-gradient(135deg, #dcfce7, #d1fae5)"
                      : "#f9fafb",
                  border:
                    carbonMechanism === comp.key
                      ? "2px solid #059669"
                      : "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "0.5rem",
                  }}
                >
                  {comp.key === "ets"
                    ? "📊"
                    : comp.key === "carbonTax"
                    ? "🏛️"
                    : "⚖️"}{" "}
                  {comp.key === "ets"
                    ? "ETS"
                    : comp.key === "carbonTax"
                    ? "Carbon Tax"
                    : "Hybrid"}
                </div>
                <div
                  style={{
                    fontSize: "1.75rem",
                    fontWeight: "bold",
                    color: carbonMechanism === comp.key ? "#059669" : "#374151",
                  }}
                >
                  ${Math.round(comp.totalCost).toLocaleString()}
                </div>
                <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                  Avg: ${Math.round(comp.avgAnnual).toLocaleString()}/year
                </div>
                {carbonMechanism === comp.key && (
                  <div style={{ marginTop: "0.5rem" }}>
                    <span style={S.badge("green")}>Currently Selected</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: "1rem",
              padding: "1rem",
              background: "#eff6ff",
              borderRadius: "0.75rem",
              fontSize: "0.875rem",
              color: "#1e40af",
            }}
          >
            <strong>💡 Recommendation:</strong> For long-term infrastructure
            projects like pavement, the <strong>Hybrid approach</strong>{" "}
            provides the best balance of price certainty and market efficiency.
            Consider sensitivity analysis across all mechanisms for risk
            assessment.
          </div>
        </div>

        <div style={S.grid2}>
          <div style={S.card}>
            <h3 style={S.cardTitle}>💼 Project Economics</h3>
            <InputField
              label="Initial Investment"
              value={economicInputs.initialInvestment}
              onChange={(v) =>
                setEconomicInputs((p) => ({ ...p, initialInvestment: v }))
              }
              unit="NZD"
            />
            <InputField
              label="Project Lifespan"
              value={economicInputs.projectLifespan}
              onChange={(v) =>
                setEconomicInputs((p) => ({ ...p, projectLifespan: v }))
              }
              unit="years"
            />
            <InputField
              label="Discount Rate"
              value={economicInputs.discountRate}
              onChange={(v) =>
                setEconomicInputs((p) => ({ ...p, discountRate: v }))
              }
              unit="%"
              helpText="NZ Treasury: 6%"
            />
            <InputField
              label="Annual Savings"
              value={economicInputs.annualSavings}
              onChange={(v) =>
                setEconomicInputs((p) => ({ ...p, annualSavings: v }))
              }
              unit="NZD/yr"
            />
            <InputField
              label="Annual Op Cost"
              value={economicInputs.annualOperatingCost}
              onChange={(v) =>
                setEconomicInputs((p) => ({ ...p, annualOperatingCost: v }))
              }
              unit="NZD/yr"
            />
          </div>

          <div style={S.card}>
            <h3 style={S.cardTitle}>
              📊 Analysis Results (
              {carbonMechanism === "ets"
                ? "ETS"
                : carbonMechanism === "carbonTax"
                ? "Carbon Tax"
                : "Hybrid"}
              )
            </h3>
            <div style={S.grid2}>
              <MetricCard
                title="NPV"
                value={`$${Math.round(economicAnalysis.npv).toLocaleString()}`}
                color={economicAnalysis.npv >= 0 ? "green" : "red"}
                icon="💰"
              />
              <MetricCard
                title="IRR"
                value={`${economicAnalysis.irr.toFixed(1)}%`}
                color={
                  economicAnalysis.irr >= economicInputs.discountRate
                    ? "green"
                    : "orange"
                }
                icon="📈"
              />
              <MetricCard
                title="Payback"
                value={`${economicAnalysis.paybackPeriod} yrs`}
                color="blue"
                icon="⏱️"
              />
              <MetricCard
                title="BCR"
                value={economicAnalysis.bcr.toFixed(2)}
                color={economicAnalysis.bcr >= 1 ? "green" : "red"}
                icon="⚖️"
              />
            </div>
            <div
              style={{
                marginTop: "1.5rem",
                padding: "1.5rem",
                borderRadius: "0.75rem",
                background:
                  economicAnalysis.npv >= 0
                    ? "linear-gradient(135deg, #dcfce7, #d1fae5)"
                    : "linear-gradient(135deg, #fee2e2, #fecaca)",
              }}
            >
              <div
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "bold",
                  color: economicAnalysis.npv >= 0 ? "#065f46" : "#991b1b",
                }}
              >
                {economicAnalysis.npv >= 0
                  ? "✅ Economically Viable"
                  : "⚠️ Needs Optimization"}
              </div>
            </div>
          </div>
        </div>

        {/* Carbon Cost Breakdown */}
        <div style={{ ...S.card, marginTop: "1.5rem" }}>
          <h3 style={S.cardTitle}>
            🌱 Carbon Cost Breakdown ({mechanism.name})
          </h3>
          <div style={S.grid4}>
            <MetricCard
              title="Total Carbon Cost"
              value={`$${Math.round(
                economicAnalysis.totalCarbonCost
              ).toLocaleString()}`}
              unit="over project life"
              color="red"
              icon="🌍"
            />
            <MetricCard
              title="NPV of Carbon Cost"
              value={`$${Math.round(
                economicAnalysis.totalDiscountedCarbonCost
              ).toLocaleString()}`}
              unit="discounted"
              color="orange"
              icon="📊"
            />
            <MetricCard
              title="Avg Annual Carbon"
              value={`$${Math.round(
                economicAnalysis.carbonCostAnnual
              ).toLocaleString()}`}
              unit="per year"
              color="purple"
              icon="📅"
            />
            <MetricCard
              title="Compliance Cost"
              value={`${mechanism.complianceCost}%`}
              unit="of carbon cost"
              color="blue"
              icon="📋"
            />
          </div>
        </div>

        {/* Year-by-Year Carbon Cost Table */}
        <div style={{ ...S.card, marginTop: "1.5rem" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h3 style={{ ...S.cardTitle, marginBottom: 0 }}>
              📅 Year-by-Year Carbon Cost Projection
            </h3>
            <button
              style={S.btnSecondary}
              onClick={() => setShowCarbonDetails(!showCarbonDetails)}
            >
              {showCarbonDetails ? "Hide Details" : "Show Details"}
            </button>
          </div>
          {showCarbonDetails && (
            <div
              style={{
                overflowX: "auto",
                maxHeight: "400px",
                overflowY: "auto",
              }}
            >
              <table style={S.table}>
                <thead style={{ position: "sticky", top: 0 }}>
                  <tr>
                    <th style={S.th}>Year</th>
                    <th style={S.th}>Carbon Price ($/t)</th>
                    <th style={S.th}>Annual Cost ($)</th>
                    <th style={S.th}>Discounted Cost ($)</th>
                    <th style={S.th}>Cumulative ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {economicAnalysis.yearlyData.slice(1).map((row, idx) => (
                    <tr key={idx}>
                      <td style={S.td}>{row.year}</td>
                      <td style={S.td}>${row.carbonPrice}</td>
                      <td style={S.td}>
                        ${Math.round(row.annualCarbonCost).toLocaleString()}
                      </td>
                      <td style={S.td}>
                        ${Math.round(row.discountedCost).toLocaleString()}
                      </td>
                      <td style={{ ...S.td, fontWeight: "600" }}>
                        ${Math.round(row.cumulativeCost).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "design":
        return <DesignTab />;
      case "traffic":
        return <TrafficTab />;
      case "materials":
        return <MaterialsTab />;
      case "biobinder":
        return <BiobinderTab />;
      case "performance":
        return <PerformanceTab />;
      case "carbon":
        return <CarbonTab />;
      case "lca":
        return <LCATab />;
      case "economic":
        return <EconomicTab />;
      default:
        return <DesignTab />;
    }
  };

  return (
    <div style={S.container}>
      <div style={S.mainCard}>
        <header style={S.header}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "1rem",
            }}
          >
            <div>
              <h1 style={S.headerTitle}>🌿 ecoroadsNZ</h1>
              <p style={{ fontSize: "1rem", opacity: 0.9 }}>
                Advanced Pavement Design & Carbon Analytics • NZTA Standards •
                ETS Integration • Biobinder Analysis
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  marginTop: "0.75rem",
                  flexWrap: "wrap",
                }}
              >
                {[
                  "NZTA P/17",
                  "EN15804 LCA",
                  "ETS Pricing",
                  "Biobinder Rheology",
                ].map((b) => (
                  <span
                    key={b}
                    style={{
                      padding: "0.25rem 0.75rem",
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "9999px",
                      fontSize: "0.75rem",
                    }}
                  >
                    {b}
                  </span>
                ))}
              </div>
            </div>
            <button
              style={{
                ...S.btnSecondary,
                background: "rgba(255,255,255,0.9)",
                color: "#1e40af",
              }}
              onClick={() => setShowExportDialog(true)}
            >
              📄 Export Report
            </button>
          </div>
        </header>
        <nav style={S.nav}>
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              style={S.navBtn(activeTab === item.id)}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <main style={S.content}>{renderContent()}</main>
      </div>

      {/* Export Dialog */}
      {showExportDialog && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "1rem",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
              width: "100%",
              maxWidth: "600px",
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <div
              style={{
                padding: "1.5rem",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(135deg, #1e40af, #7c3aed)",
              }}
            >
              <h3
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "bold",
                  color: "white",
                }}
              >
                📄 Export Analysis Report
              </h3>
              <button
                onClick={() => setShowExportDialog(false)}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  color: "white",
                  borderRadius: "0.5rem",
                  width: "2rem",
                  height: "2rem",
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: "1.5rem" }}>
              <div style={{ marginBottom: "1.5rem" }}>
                <label style={S.label}>Select Sections to Include</label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "0.5rem",
                    marginTop: "0.5rem",
                  }}
                >
                  {[
                    {
                      key: "design",
                      label: "🌿 Design Parameters",
                      icon: "🌿",
                    },
                    {
                      key: "traffic",
                      label: "🚛 Traffic Analysis",
                      icon: "🚛",
                    },
                    {
                      key: "materials",
                      label: "🧱 Materials Selection",
                      icon: "🧱",
                    },
                    {
                      key: "biobinder",
                      label: "🧪 Biobinder Rheology",
                      icon: "🧪",
                    },
                    { key: "performance", label: "📊 Performance", icon: "📊" },
                    { key: "carbon", label: "🌱 Carbon Analysis", icon: "🌱" },
                    { key: "lca", label: "♻️ LCA", icon: "♻️" },
                    { key: "economic", label: "💰 Economic", icon: "💰" },
                  ].map((section) => (
                    <label
                      key={section.key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        padding: "0.75rem",
                        background: exportSections[section.key]
                          ? "#ecfdf5"
                          : "#f9fafb",
                        borderRadius: "0.5rem",
                        cursor: "pointer",
                        border: exportSections[section.key]
                          ? "2px solid #059669"
                          : "2px solid #e5e7eb",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={exportSections[section.key]}
                        onChange={(e) =>
                          setExportSections((p) => ({
                            ...p,
                            [section.key]: e.target.checked,
                          }))
                        }
                        style={{ width: "1.25rem", height: "1.25rem" }}
                      />
                      <span style={{ fontSize: "0.875rem", fontWeight: "500" }}>
                        {section.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ ...S.infoBox("info"), marginBottom: "1rem" }}>
                <strong>Report Contents:</strong>
                <ul
                  style={{
                    marginTop: "0.5rem",
                    marginLeft: "1.25rem",
                    fontSize: "0.875rem",
                  }}
                >
                  <li>Full project parameters and climate data</li>
                  <li>Traffic analysis with ESA calculations</li>
                  <li>Materials specifications and properties</li>
                  <li>Biobinder rheology comparison</li>
                  <li>Performance predictions (if run)</li>
                  <li>Carbon analysis with ETS projections</li>
                  <li>Complete LCA breakdown by EN15804 modules</li>
                  <li>Economic analysis with all carbon pricing mechanisms</li>
                </ul>
              </div>

              <div
                style={{
                  background: "#f9fafb",
                  padding: "1rem",
                  borderRadius: "0.75rem",
                  marginBottom: "1rem",
                }}
              >
                <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                  <strong>Project:</strong> {nzLocation} - {roadClassification}
                  <br />
                  <strong>Biobinder:</strong>{" "}
                  {useBiobinderInAnalysis
                    ? `✅ Enabled (${effectiveBinderProps.name})`
                    : "❌ Not enabled"}
                  <br />
                  <strong>LCA Entries:</strong> {allLcaEntries.length} (
                  {autoLcaEntries.length} auto + {lcaEntries.length} manual)
                  <br />
                  <strong>Carbon Mechanism:</strong>{" "}
                  {carbonMechanism === "ets"
                    ? "ETS"
                    : carbonMechanism === "carbonTax"
                    ? "Carbon Tax"
                    : "Hybrid"}
                </div>
              </div>
            </div>
            <div
              style={{
                padding: "1.5rem",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                gap: "1rem",
                background: "#f9fafb",
              }}
            >
              <button
                style={S.btnSecondary}
                onClick={() => setShowExportDialog(false)}
              >
                Cancel
              </button>
              <button style={S.btnPrimary} onClick={generateReport}>
                📥 Download HTML Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
